// === IMPORTS ===
import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import { Trash2, ZoomIn, ZoomOut, Focus, Undo2, Zap, Camera, History } from 'lucide-react';
import {
  OfficeLayout,
  PlacedFurniture,
  AIAgent,
  GridPos,
  EditorTool,
  FurnitureType,
  OfficeRoom,
  AgentInfoCategory,
} from '../types';
import {
  gridToIso,
  isoToGrid,
  TILE_WIDTH,
  TILE_HEIGHT,
  isCellWalkable,
  isInsideRoom,
  findOverlappingRoom,
} from '../utils/isoMath';
import { FURNITURE_CATALOG, DEFAULT_FLOOR_MATERIALS, RoomTemplate } from '../data/officeCatalog';
import { AgentContextMenu, DynamicActionType } from './AgentContextMenu';
import { TileContextMenu } from './TileContextMenu';
import { FloorMaterialOption, IsoTileType, SpriteDirection } from '../types';
import { VisualThemeId } from '../types/theme';
import { getThemeConfig } from '../utils/themePresets';
import {
  getIsometric4Direction,
  getFrameKey,
  getOrLoadSpriteImage,
  generateProceduralSpriteFrame,
} from '../utils/agentSpriteEngine';
import { resolveColorToHex, getCheckerboardColorTones } from '../utils/floorColorUtils';
import './IsometricCanvas.css';

export type RoomVertexHandle = 'top' | 'right' | 'bottom' | 'left';

export interface HoveredRoomVertex {
  roomId: string;
  handle: RoomVertexHandle;
  room: OfficeRoom;
  x: number;
  y: number;
  cursor: string;
  label: string;
}

export interface ResizingRoomState {
  roomId: string;
  handle: RoomVertexHandle;
  initialRoom: OfficeRoom;
  currentRoom: OfficeRoom;
}

function getRoomVertices(room: OfficeRoom, originX = 0, originY = 0) {
  const hw = TILE_WIDTH / 2;
  const hh = TILE_HEIGHT / 2;

  const topTile = gridToIso(room.col, room.row, 0, originX, originY);
  const rightTile = gridToIso(room.col + room.width - 1, room.row, 0, originX, originY);
  const botTile = gridToIso(room.col + room.width - 1, room.row + room.height - 1, 0, originX, originY);
  const leftTile = gridToIso(room.col, room.row + room.height - 1, 0, originX, originY);

  const pTop = { x: topTile.x, y: topTile.y };
  const pRight = { x: rightTile.x + hw, y: rightTile.y + hh };
  const pBot = { x: botTile.x, y: botTile.y + TILE_HEIGHT };
  const pLeft = { x: leftTile.x - hw, y: leftTile.y + hh };

  return [
    { handle: 'top' as const, ...pTop, cursor: 'nwse-resize', label: 'North Vertex (Origin)' },
    { handle: 'right' as const, ...pRight, cursor: 'nesw-resize', label: 'East Vertex (Width)' },
    { handle: 'bottom' as const, ...pBot, cursor: 'nwse-resize', label: 'South Vertex (Area)' },
    { handle: 'left' as const, ...pLeft, cursor: 'nesw-resize', label: 'West Vertex (Length)' },
  ];
}

interface IsometricCanvasProps {
  layout: OfficeLayout;
  activeTool?: EditorTool;
  selectedFurnitureType?: FurnitureType | null;
  selectedItemId?: string | null;
  selectedAgentId?: string | null;
  selectedRoomId?: string | null;
  floorMaterialsMap?: Record<string, { customImage?: string }>;
  roomDraftBounds?: { col: number; row: number; width: number; height: number; color?: string; name?: string; icon?: string } | null;
  floorMaterials?: FloorMaterialOption[];
  onClearTile?: (col: number, row: number) => void;
  onClearTileFurniture?: (col: number, row: number) => void;
  onClearTileFloor?: (col: number, row: number) => void;
  onClearTileAgent?: (col: number, row: number) => void;
  onSetTileFloor?: (col: number, row: number, tileType: IsoTileType) => void;
  onStartRoomDraft?: (col: number, row: number) => void;
  onCreateRoom?: (col: number, row: number, template?: RoomTemplate | Partial<OfficeRoom>) => void;
  onTileClick?: (col: number, row: number) => void;
  onPaintTile?: (col: number, row: number, isErasing: boolean) => void;
  onItemSelect?: (item: PlacedFurniture | null) => void;
  onAgentSelect?: (agent: AIAgent | null, initialTab?: AgentInfoCategory) => void;
  onRoomSelect?: (roomId: string | null) => void;
  onDeleteRoom?: (roomId: string) => void;
  onRoomUpdate?: (room: OfficeRoom) => void;
  onOverlapPrevented?: (targetRoomName: string, conflictingRoomName: string) => void;
  onRoomAreaDefined?: (col: number, row: number, width: number, height: number) => void;
  onMoveToRoom?: (agentId: string, room: OfficeRoom) => void;
  onDynamicAgentAction?: (agentId: string, actionType: DynamicActionType) => void;
  onDropFurniture?: (type: FurnitureType, col: number, row: number) => void;
  onDropAgent?: (agentId: string, col: number, row: number) => void;
  onUndo?: () => void;
  canUndo?: boolean;
  onResetFloor?: () => void;
  isSimulating?: boolean;
  isSimulationPlaying?: boolean;
  onToggleSimulationPlayPause?: () => void;
  isTimeMachineReplay?: boolean;
  timeMachineClockTime?: string;
  onGoLiveTimeMachine?: () => void;
  onOpenTimeMachineInspector?: () => void;
  timeMachineIncidentText?: string;
  visualThemeId?: VisualThemeId;
  locationSelector?: React.ReactNode;
  topRightOverlay?: React.ReactNode;
  onOpenInteractions?: () => void;
  onOpenSnapshotManager?: () => void;
  onOpenTimeMachine?: () => void;
}

// === COMPONENTE: CANVAS ISOMÉTRICO PRINCIPAL (GRID, PISO, MOBILIARIO, AGENTES) ===
export const IsometricCanvas: React.FC<IsometricCanvasProps> = ({
  layout,
  activeTool = 'select',
  selectedFurnitureType,
  selectedItemId,
  selectedAgentId,
  selectedRoomId,
  visualThemeId = 'twopoint',
  locationSelector,
  topRightOverlay,
  floorMaterialsMap,
  floorMaterials = DEFAULT_FLOOR_MATERIALS,
  roomDraftBounds,
  onClearTile,
  onClearTileFurniture,
  onClearTileFloor,
  onClearTileAgent,
  onSetTileFloor,
  onStartRoomDraft,
  onCreateRoom,
  onTileClick,
  onPaintTile,
  onItemSelect,
  onAgentSelect,
  onRoomSelect,
  onDeleteRoom,
  onRoomUpdate,
  onOverlapPrevented,
  onMoveToRoom,
  onDynamicAgentAction,
  onDropFurniture,
  onDropAgent,
  onUndo,
  canUndo = false,
  onResetFloor,
  onOpenInteractions,
  onOpenSnapshotManager,
  onOpenTimeMachine,
  isSimulating = false,
  isSimulationPlaying = true,
  onToggleSimulationPlayPause,
  isTimeMachineReplay = false,
  timeMachineClockTime,
  onGoLiveTimeMachine,
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const themeConfig = useMemo(() => getThemeConfig((visualThemeId as VisualThemeId) || 'twopoint'), [visualThemeId]);

  // Viewport transformation state (Pan & Zoom)
  const [zoom, setZoom] = useState<number>(1.1);
  const [pan, setPan] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState<boolean>(false);
  const [isMouseDown, setIsMouseDown] = useState<boolean>(false);
  const [startPan, setStartPan] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [mouseDownPos, setMouseDownPos] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [hasDragged, setHasDragged] = useState<boolean>(false);

  // Hover state
  const [hoverGrid, setHoverGrid] = useState<GridPos | null>(null);

  // Interactive Room Vertex Handle States
  const [hoveredVertex, setHoveredVertex] = useState<HoveredRoomVertex | null>(null);
  const [resizingRoom, setResizingRoom] = useState<ResizingRoomState | null>(null);

  // Right-Click Context Menu States (Agent and Tile)
  const [contextMenu, setContextMenu] = useState<{ agent: AIAgent; x: number; y: number } | null>(null);
  const [tileContextMenu, setTileContextMenu] = useState<{ col: number; row: number; x: number; y: number } | null>(null);

  // Close context menu if simulation mode turns off
  useEffect(() => {
    if (!isSimulating) {
      setContextMenu(null);
    }
  }, [isSimulating]);

  // Reference to track container dimensions and keep grid synchronized with floating bars
  const prevSizeRef = useRef<{ width: number; height: number } | null>(null);

  // Auto-center pan on layout load (when grid dimensions change or first load)
  useEffect(() => {
    if (containerRef.current) {
      const width = containerRef.current.clientWidth;
      const height = containerRef.current.clientHeight;
      setPan({ x: width / 2, y: height / 4 });
      prevSizeRef.current = { width, height };
    }
  }, [layout.gridWidth, layout.gridHeight]);

  // Keep grid position locked relative to center floating bar during container resizing / sidebar toggles
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    if (!prevSizeRef.current) {
      prevSizeRef.current = {
        width: container.clientWidth,
        height: container.clientHeight,
      };
    }

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        if (width <= 0 || height <= 0) continue;

        if (prevSizeRef.current && prevSizeRef.current.width > 0) {
          const deltaX = (width - prevSizeRef.current.width) / 2;
          const deltaY = (height - prevSizeRef.current.height) / 4;

          if (deltaX !== 0 || deltaY !== 0) {
            setPan((prev) => ({
              x: prev.x + deltaX,
              y: prev.y + (deltaY !== 0 ? deltaY : 0),
            }));
          }
        }

        prevSizeRef.current = { width, height };
      }
    });

    resizeObserver.observe(container);
    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // Handle Zoom with smooth, controlled sensitivity
  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    if (contextMenu) setContextMenu(null);
    if (tileContextMenu) setTileContextMenu(null);
    const clampedDelta = Math.max(-80, Math.min(80, e.deltaY));
    const zoomMultiplier = Math.exp(-clampedDelta * 0.0012);
    setZoom((prev) => Math.min(Math.max(prev * zoomMultiplier, 0.35), 3.0));
  };

  // Handle Pan & Drag start
  const handleMouseDown = (e: React.MouseEvent) => {
    if (contextMenu) setContextMenu(null);
    if (tileContextMenu) setTileContextMenu(null);

    if (!isSimulating && hoveredVertex) {
      setResizingRoom({
        roomId: hoveredVertex.roomId,
        handle: hoveredVertex.handle,
        initialRoom: { ...hoveredVertex.room },
        currentRoom: { ...hoveredVertex.room },
      });
      setIsMouseDown(true);
      setHasDragged(true);
      setIsPanning(false);
      setMouseDownPos({ x: e.clientX, y: e.clientY });
      if (onRoomSelect) onRoomSelect(hoveredVertex.roomId);
      return;
    }

    if (e.button === 0 || e.button === 1) {
      setIsPanning(true);
      setIsMouseDown(true);
      setHasDragged(false);
      setMouseDownPos({ x: e.clientX, y: e.clientY });
      setStartPan({ x: e.clientX - pan.x, y: e.clientY - pan.y });
    }
  };

  // Center camera directly on agent coordinates
  const handleFocusCameraOnAgent = (agent: AIAgent) => {
    const { x: isoX, y: isoY } = gridToIso(agent.currentCol, agent.currentRow, 0, 0, 0);
    if (containerRef.current) {
      const w = containerRef.current.clientWidth;
      const h = containerRef.current.clientHeight;
      setPan({
        x: w / 2 - isoX * zoom,
        y: h / 2 - (isoY - 18) * zoom,
      });
    }
  };

  // Helper to find an agent at screen isometric coordinates or grid coordinate
  const findAgentAtPoint = (
    mouseX: number,
    mouseY: number,
    gridPos: { col: number; row: number } | null
  ): AIAgent | null => {
    const placedAgents = (layout.agents || []).filter(
      (a) => a.isPlaced !== false && a.currentCol >= 0 && a.currentRow >= 0
    );
    const sortedAgents = [...placedAgents].sort(
      (a, b) => b.currentCol + b.currentRow - (a.currentCol + a.currentRow)
    );

    for (const agent of sortedAgents) {
      const { x, y } = gridToIso(agent.currentCol, agent.currentRow, 0, 0, 0);
      const dx = Math.abs(mouseX - x);
      const dy = mouseY - (y - 18);
      if (dx <= 24 && dy >= -28 && dy <= 28) {
        return agent;
      }
    }

    if (gridPos) {
      for (const agent of sortedAgents) {
        const dCol = Math.abs(agent.currentCol - gridPos.col);
        const dRow = Math.abs(agent.currentRow - gridPos.row);
        if (dCol < 0.95 && dRow < 0.95) {
          return agent;
        }
      }
    }

    return null;
  };

  // Handle Mouse Move for Hover, Resizing & Panning
  const handleMouseMove = (e: React.MouseEvent) => {
    if (!canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const mouseX = (e.clientX - rect.left - pan.x) / zoom;
    const mouseY = (e.clientY - rect.top - pan.y) / zoom;

    if (isMouseDown) {
      const dist = Math.hypot(e.clientX - mouseDownPos.x, e.clientY - mouseDownPos.y);
      if (dist > 4) {
        setHasDragged(true);
      }
    }

    if (resizingRoom && isMouseDown) {
      setHasDragged(true);
      const curGrid = isoToGrid(mouseX, mouseY, 0, 0);
      const init = resizingRoom.initialRoom;
      let newCol = init.col;
      let newRow = init.row;
      let newWidth = init.width;
      let newHeight = init.height;

      if (resizingRoom.handle === 'bottom') {
        newWidth = Math.max(1, Math.min(layout.gridWidth - init.col, curGrid.col - init.col + 1));
        newHeight = Math.max(1, Math.min(layout.gridHeight - init.row, curGrid.row - init.row + 1));
      } else if (resizingRoom.handle === 'top') {
        const fixedBRCol = init.col + init.width - 1;
        const fixedBRRow = init.row + init.height - 1;
        newCol = Math.max(0, Math.min(curGrid.col, fixedBRCol));
        newRow = Math.max(0, Math.min(curGrid.row, fixedBRRow));
        newWidth = fixedBRCol - newCol + 1;
        newHeight = fixedBRRow - newRow + 1;
      } else if (resizingRoom.handle === 'right') {
        const fixedBRRow = init.row + init.height - 1;
        newWidth = Math.max(1, Math.min(layout.gridWidth - init.col, curGrid.col - init.col + 1));
        newRow = Math.max(0, Math.min(curGrid.row, fixedBRRow));
        newHeight = fixedBRRow - newRow + 1;
      } else if (resizingRoom.handle === 'left') {
        const fixedBRCol = init.col + init.width - 1;
        newCol = Math.max(0, Math.min(curGrid.col, fixedBRCol));
        newWidth = fixedBRCol - newCol + 1;
        newHeight = Math.max(1, Math.min(layout.gridHeight - init.row, curGrid.row - init.row + 1));
      }

      const updatedRoom: OfficeRoom = {
        ...init,
        col: newCol,
        row: newRow,
        width: newWidth,
        height: newHeight,
      };

      setResizingRoom((prev) => (prev ? { ...prev, currentRoom: updatedRoom } : null));
      return;
    }

    if (isPanning) {
      setPan({ x: e.clientX - startPan.x, y: e.clientY - startPan.y });
    }

    if (tileContextMenu) {
      if (!hoverGrid || hoverGrid.col !== tileContextMenu.col || hoverGrid.row !== tileContextMenu.row) {
        setHoverGrid({ col: tileContextMenu.col, row: tileContextMenu.row });
      }
      return;
    }

    const gridPos = isoToGrid(mouseX, mouseY, 0, 0);
    if (
      gridPos.col >= 0 &&
      gridPos.col < layout.gridWidth &&
      gridPos.row >= 0 &&
      gridPos.row < layout.gridHeight
    ) {
      setHoverGrid(gridPos);
    } else {
      setHoverGrid(null);
    }

    if (!isSimulating && layout.rooms && layout.rooms.length > 0) {
      let detectedVertex: HoveredRoomVertex | null = null;
      const hitRadius = Math.max(22 / zoom, 18);

      const roomsToCheck = selectedRoomId
        ? [
            ...layout.rooms.filter((r) => r.id === selectedRoomId),
            ...layout.rooms.filter((r) => r.id !== selectedRoomId),
          ]
        : layout.rooms;

      for (const room of roomsToCheck) {
        const vertices = getRoomVertices(room);
        for (const v of vertices) {
          const dist = Math.hypot(mouseX - v.x, mouseY - v.y);
          if (dist <= hitRadius) {
            detectedVertex = {
                roomId: room.id,
                handle: v.handle,
                room,
                x: v.x,
                y: v.y,
                cursor: v.cursor,
                label: v.label,
                };
            break;
          }
        }
        if (detectedVertex) break;
      }
      setHoveredVertex(detectedVertex);
    } else {
      setHoveredVertex(null);
    }
  };

  const handleMouseUp = () => {
    if (resizingRoom) {
      const conflictingRoom = findOverlappingRoom(
        resizingRoom.currentRoom,
        layout.rooms?.filter((r) => r.id !== resizingRoom.roomId)
      );

      if (conflictingRoom) {
        if (onOverlapPrevented) {
          onOverlapPrevented(resizingRoom.currentRoom.name, conflictingRoom.name);
        }
      } else {
        if (onRoomUpdate) {
          onRoomUpdate(resizingRoom.currentRoom);
        }
      }

      setResizingRoom(null);
      setIsPanning(false);
      setIsMouseDown(false);
      return;
    }

    setIsPanning(false);
    setIsMouseDown(false);
  };

  // Handle Mouse Click for selection/placement
  const handleClick = (e: React.MouseEvent) => {
    if (hasDragged) return;

    if (!canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const mouseX = (e.clientX - rect.left - pan.x) / zoom;
    const mouseY = (e.clientY - rect.top - pan.y) / zoom;
    const gridPos = isoToGrid(mouseX, mouseY, 0, 0);

    if (isSimulating) {
      const clickedAgent = findAgentAtPoint(mouseX, mouseY, gridPos);

      if (clickedAgent && onAgentSelect) {
        onAgentSelect(clickedAgent);
      } else if (onAgentSelect) {
        onAgentSelect(null);
      }
      return;
    }

    if (!hoverGrid) return;

    if (selectedAgentId) {
      if (onTileClick) {
        onTileClick(hoverGrid.col, hoverGrid.row);
        return;
      }
    }

    if ((activeTool === 'paint_floor' || activeTool === 'erase_floor') && onPaintTile) {
      onPaintTile(hoverGrid.col, hoverGrid.row, activeTool === 'erase_floor');
      return;
    }

    if (activeTool === 'place' && selectedFurnitureType) {
      if (onTileClick) {
        onTileClick(hoverGrid.col, hoverGrid.row);
        return;
      }
    }

    if (activeTool === 'place' || activeTool === 'select') {
      if (onTileClick) {
        onTileClick(hoverGrid.col, hoverGrid.row);
      }
    }

    const clickedAgent = findAgentAtPoint(mouseX, mouseY, hoverGrid);
    if (clickedAgent && onAgentSelect) {
      if (selectedAgentId === clickedAgent.id) {
        onAgentSelect(null);
      } else {
        onAgentSelect(clickedAgent);
        if (onItemSelect) onItemSelect(null);
        if (onRoomSelect) onRoomSelect(null);
      }
      return;
    }

    const clickedItem = layout.items.find((item) => {
      const def = FURNITURE_CATALOG.find((f) => f.type === item.type);
      const w = item.width || def?.width || 1;
      const h = item.height || def?.height || 1;
      return (
        hoverGrid.col >= item.col &&
        hoverGrid.col < item.col + w &&
        hoverGrid.row >= item.row &&
        hoverGrid.row < item.row + h
      );
    });

    if (clickedItem) {
      if (onItemSelect) {
        if (selectedItemId === clickedItem.id) {
          onItemSelect(null);
        } else {
          onItemSelect(clickedItem);
          if (onAgentSelect) onAgentSelect(null);
          if (onRoomSelect) onRoomSelect(null);
        }
      }
      return;
    }

    const clickedRoom = (layout.rooms || []).find((r) => isInsideRoom(hoverGrid.col, hoverGrid.row, r));
    if (clickedRoom && onRoomSelect) {
      if (selectedRoomId === clickedRoom.id) {
        onRoomSelect(null);
      } else {
        onRoomSelect(clickedRoom.id);
        if (onItemSelect) onItemSelect(null);
        if (onAgentSelect) onAgentSelect(null);
      }
      return;
    }

    if (onItemSelect) onItemSelect(null);
    if (onAgentSelect) onAgentSelect(null);
    if (onRoomSelect) onRoomSelect(null);
  };

  // === CICLO PRINCIPAL DE RENDERIZADO (CANVAS 2D) ===
  const drawScene = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    if (containerRef.current) {
      canvas.width = containerRef.current.clientWidth;
      canvas.height = containerRef.current.clientHeight;
    }

    ctx.imageSmoothingEnabled = false;
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    ctx.save();
    ctx.translate(pan.x, pan.y);
    ctx.scale(zoom, zoom);

    const originX = 0;
    const originY = 0;

    // 1. Draw Ground Ambient Shadow under Base Platform
    {
      const { x: topX, y: topY } = gridToIso(0, 0, 0, originX, originY);
      const { x: rightX, y: rightY } = gridToIso(layout.gridWidth, 0, 0, originX, originY);
      const { x: bottomX, y: bottomY } = gridToIso(layout.gridWidth, layout.gridHeight, 0, originX, originY);
      const { x: leftX, y: leftY } = gridToIso(0, layout.gridHeight, 0, originX, originY);

      ctx.save();
      ctx.fillStyle = 'rgba(2, 6, 23, 0.55)';
      ctx.beginPath();
      ctx.moveTo(topX, topY + 12);
      ctx.lineTo(rightX + 16, rightY + 22);
      ctx.lineTo(bottomX, bottomY + 36);
      ctx.lineTo(leftX - 16, leftY + 22);
      ctx.closePath();
      ctx.fill();
      ctx.restore();
    }

    // 2. Draw Base Board & Floor Tiles
    for (let r = 0; r < layout.gridHeight; r++) {
      for (let c = 0; c < layout.gridWidth; c++) {
        const { x, y } = gridToIso(c, r, 0, originX, originY);
        const customTile = layout.customFloors ? layout.customFloors[`${c},${r}`] : null;

        if (customTile) {
          const materialDef = floorMaterials.find((f) => f.id === customTile.tileType);
          const customImg = materialDef?.customImage || (floorMaterialsMap && floorMaterialsMap[customTile.tileType]?.customImage) || customTile.customImage;
          const customImgSec = materialDef?.customImageSecondary || customTile.customImageSecondary;

          drawFloorTile(
            ctx,
            x,
            y,
            c,
            r,
            customTile.tileType,
            customTile.roomName,
            customImg,
            layout.gridWidth,
            layout.gridHeight,
            layout.customFloors,
            materialDef,
            customImgSec
          );
        } else {
          drawBaseBoardTile(
            ctx,
            x,
            y,
            c,
            r,
            layout.gridWidth,
            layout.gridHeight,
            layout.customFloors,
            themeConfig
          );
        }
      }
    }

    // Draw Spatial Rooms Overlays & Visual Boundaries (Only rendered visually in Editor mode; hidden in Simulation)
    if (layout.rooms && layout.rooms.length > 0 && !isSimulating) {
      for (const room of layout.rooms) {
        const isSelected = selectedRoomId === room.id;
        const agentsInRoom = (layout.agents || []).filter((ag) =>
          ag.isPlaced !== false && ag.currentCol >= 0 && ag.currentRow >= 0 && isInsideRoom(ag.currentCol, ag.currentRow, room)
        ).length;

        const isThisResizing = resizingRoom && resizingRoom.roomId === room.id;
        const roomToRender = isThisResizing ? resizingRoom.currentRoom : room;

        const isOverlapping = isThisResizing
          ? Boolean(findOverlappingRoom(roomToRender, layout.rooms?.filter((r) => r.id !== room.id)))
          : false;

        const hoveredHandle = hoveredVertex && hoveredVertex.roomId === room.id ? hoveredVertex.handle : null;
        const activeResizingHandle = isThisResizing ? resizingRoom.handle : null;

        drawRoomOverlay(
          ctx,
          roomToRender,
          isSelected || Boolean(isThisResizing),
          agentsInRoom,
          originX,
          originY,
          true,
          hoveredHandle,
          activeResizingHandle,
          isOverlapping,
          false
        );
      }
    }

    // Draw Room Area Drafting Preview (if active drafting in editor)
    if (roomDraftBounds && !isSimulating) {
      const draftRoom: OfficeRoom = {
        id: 'draft_preview',
        name: roomDraftBounds.name || 'Nueva Zona',
        type: 'custom',
        color: roomDraftBounds.color || '#38bdf8',
        icon: roomDraftBounds.icon || '📍',
        col: roomDraftBounds.col,
        row: roomDraftBounds.row,
        width: roomDraftBounds.width,
        height: roomDraftBounds.height,
        policy: { energyDelta: 0, focusBonus: 0, bladderDelta: 0 },
      };
      const isDraftOverlapping = Boolean(findOverlappingRoom(draftRoom, layout.rooms));
      drawRoomOverlay(ctx, draftRoom, true, 0, originX, originY, true, null, null, isDraftOverlapping, false);
    }

    // Draw Hover Grid Highlight & Tool Previews (Only active in Editor mode)
    if (hoverGrid && !isSimulating) {
      const { x, y } = gridToIso(hoverGrid.col, hoverGrid.row, 0, originX, originY);

      if (activeTool === 'paint_floor') {
        drawTileHighlight(ctx, x, y, '#22c55e', 0.6);
      } else if (activeTool === 'erase_floor') {
        drawTileHighlight(ctx, x, y, '#ef4444', 0.6);
      } else if (activeTool === 'place' && selectedFurnitureType) {
        const def = FURNITURE_CATALOG.find((f) => f.type === selectedFurnitureType);
        if (def) {
          const itemW = def.width || 1;
          const itemH = def.height || 1;

          let allWalkable = true;
          for (let bc = 0; bc < itemW; bc++) {
            for (let br = 0; br < itemH; br++) {
              if (
                !isCellWalkable(
                  hoverGrid.col + bc,
                  hoverGrid.row + br,
                  layout.gridWidth,
                  layout.gridHeight,
                  layout.items,
                  layout.customFloors
                )
              ) {
                allWalkable = false;
                break;
              }
            }
            if (!allWalkable) break;
          }

          for (let bc = 0; bc < itemW; bc++) {
            for (let br = 0; br < itemH; br++) {
              const { x: hx, y: hy } = gridToIso(
                hoverGrid.col + bc,
                hoverGrid.row + br,
                0,
                originX,
                originY
              );
              drawTileHighlight(ctx, hx, hy, allWalkable ? '#22c55e' : '#ef4444', 0.6);
            }
          }

          const ghostItem: PlacedFurniture = {
            id: 'ghost_preview',
            type: selectedFurnitureType,
            col: hoverGrid.col,
            row: hoverGrid.row,
            rotation: 0,
            width: itemW,
            height: itemH,
            customImage: def.customImage,
          };
          ctx.save();
          ctx.globalAlpha = 0.65;
          const { x: gx, y: gy } = gridToIso(hoverGrid.col, hoverGrid.row, 0, originX, originY);
          drawFurnitureItem(ctx, gx, gy, ghostItem, false, false);
          ctx.restore();
        }
      } else if (selectedAgentId) {
        drawTileHighlight(ctx, x, y, '#10b981', 0.6);

        const ghostAgent = layout.agents.find((a) => a.id === selectedAgentId);
        if (ghostAgent) {
          ctx.save();
          ctx.globalAlpha = 0.65;
          drawAgentSprite(ctx, x, y, ghostAgent, true, isSimulating, false, false);
          ctx.restore();
        }
      } else {
        drawTileHighlight(ctx, x, y, '#38bdf8', 0.4);
      }
    }

    // Dedicated persistent highlight for tileContextMenu in any mode (Editor or Simulation)
    if (tileContextMenu) {
      const { x: tcx, y: tcy } = gridToIso(tileContextMenu.col, tileContextMenu.row, 0, originX, originY);
      drawTileHighlight(ctx, tcx, tcy, '#06b6d4', 0.6);
    }

    // Collect and Sort Depth Objects (Furniture + Agents)
    type RenderObject =
      | { kind: 'furniture'; item: PlacedFurniture; depth: number }
      | { kind: 'agent'; agent: AIAgent; depth: number };

    const renderList: RenderObject[] = [];

    layout.items.forEach((item) => {
      renderList.push({
        kind: 'furniture',
        item,
        depth: item.col + item.row,
      });
    });

    (layout.agents || [])
      .filter((agent) => agent.isPlaced !== false && agent.currentCol >= 0 && agent.currentRow >= 0)
      .forEach((agent) => {
        renderList.push({
          kind: 'agent',
          agent,
          depth: agent.currentCol + agent.currentRow + 0.2,
        });
      });

    renderList.sort((a, b) => a.depth - b.depth);

    renderList.forEach((obj) => {
      if (obj.kind === 'furniture') {
        const item = obj.item;
        const { x, y } = gridToIso(item.col, item.row, 0, originX, originY);
        const isSelected = item.id === selectedItemId;
        const isOccluding = isItemOccludingHover(item, hoverGrid, originX, originY);
        drawFurnitureItem(ctx, x, y, item, isSelected, isOccluding);
      } else {
        const agent = obj.agent;
        const { x, y } = gridToIso(agent.currentCol, agent.currentRow, 0, originX, originY);
        const isSelected = agent.id === selectedAgentId;
        const isHovered = Boolean(
          isSimulating &&
            hoverGrid &&
            ((Math.abs(agent.currentCol - hoverGrid.col) < 0.85 &&
              Math.abs(agent.currentRow - hoverGrid.row) < 0.85) ||
              (Math.round(agent.currentCol) === hoverGrid.col &&
                Math.round(agent.currentRow) === hoverGrid.row))
        );
        const isOccluding = isAgentOccludingHover(agent, hoverGrid, originX, originY);
        drawAgentSprite(ctx, x, y, agent, isSelected, isSimulating, isOccluding, isHovered);
      }
    });

    ctx.restore();
  }, [
    layout,
    zoom,
    pan,
    hoverGrid,
    activeTool,
    selectedFurnitureType,
    selectedItemId,
    selectedAgentId,
    selectedRoomId,
    hoveredVertex,
    resizingRoom,
    roomDraftBounds,
    floorMaterialsMap,
    isSimulating,
    tileContextMenu,
  ]);

  useEffect(() => {
    let animId: number;
    const render = () => {
      drawScene();
      animId = requestAnimationFrame(render);
    };
    render();
    return () => cancelAnimationFrame(animId);
  }, [drawScene]);

  // Check if hovering an agent in simulation mode to toggle pointer cursor
  const isHoveringAgent = Boolean(
    isSimulating &&
      hoverGrid &&
      (layout.agents || []).some((a) => {
        if (a.isPlaced === false || a.currentCol < 0 || a.currentRow < 0) return false;
        const dCol = Math.abs(a.currentCol - hoverGrid.col);
        const dRow = Math.abs(a.currentRow - hoverGrid.row);
        return (
          (dCol < 0.85 && dRow < 0.85) ||
          (Math.round(a.currentCol) === hoverGrid.col &&
            Math.round(a.currentRow) === hoverGrid.row)
        );
      })
  );

  // Determine dynamic container cursor
  const containerCursor = useMemo(() => {
    if (resizingRoom) {
      if (resizingRoom.handle === 'top' || resizingRoom.handle === 'bottom') return 'nwse-resize';
      return 'nesw-resize';
    }
    if (hoveredVertex) {
      return hoveredVertex.cursor;
    }
    if (isPanning && hasDragged) return 'grabbing';
    if (isHoveringAgent) return 'pointer';
    return 'grab';
  }, [resizingRoom, hoveredVertex, isPanning, hasDragged, isHoveringAgent]);

  // === RENDER ===
  return (
    <div
      ref={containerRef}
      style={{ cursor: containerCursor }}
      className="twin-canvas-container"
      onWheel={handleWheel}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onClick={handleClick}
      onContextMenu={(e) => {
        e.preventDefault();
        e.stopPropagation();

        if (!canvasRef.current) return;

        const rect = canvasRef.current.getBoundingClientRect();
        const mouseX = (e.clientX - rect.left - pan.x) / zoom;
        const mouseY = (e.clientY - rect.top - pan.y) / zoom;
        const gridPos = isoToGrid(mouseX, mouseY, 0, 0);

        if (
          gridPos.col >= 0 &&
          gridPos.col < layout.gridWidth &&
          gridPos.row >= 0 &&
          gridPos.row < layout.gridHeight
        ) {
          if (isSimulating) {
            const clickedAgent = findAgentAtPoint(mouseX, mouseY, gridPos);
            if (clickedAgent) {
              setTileContextMenu(null);
              setContextMenu({
                agent: clickedAgent,
                x: e.clientX,
                y: e.clientY,
              });
              return;
            }
          }

          setContextMenu(null);
          setHoverGrid({ col: gridPos.col, row: gridPos.row });
          setTileContextMenu({
            col: gridPos.col,
            row: gridPos.row,
            x: e.clientX,
            y: e.clientY,
          });
        } else {
          setContextMenu(null);
          setTileContextMenu(null);
        }
      }}
      onDragOver={(e) => {
        if (isSimulating) {
          e.preventDefault();
          return;
        }
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (canvasRef.current) {
          const rect = canvasRef.current.getBoundingClientRect();
          const mouseX = (e.clientX - rect.left - pan.x) / zoom;
          const mouseY = (e.clientY - rect.top - pan.y) / zoom;
          const gridPos = isoToGrid(mouseX, mouseY, 0, 0);
          if (
            gridPos.col >= 0 &&
            gridPos.col < layout.gridWidth &&
            gridPos.row >= 0 &&
            gridPos.row < layout.gridHeight
          ) {
            if (!hoverGrid || hoverGrid.col !== gridPos.col || hoverGrid.row !== gridPos.row) {
              setHoverGrid(gridPos);
            }
          }
        }
      }}
      onDragLeave={() => {
        setHoverGrid(null);
      }}
      onDrop={(e) => {
        if (isSimulating) {
          e.preventDefault();
          return;
        }
        e.preventDefault();
        if (!canvasRef.current) return;
        const rect = canvasRef.current.getBoundingClientRect();
        const mouseX = (e.clientX - rect.left - pan.x) / zoom;
        const mouseY = (e.clientY - rect.top - pan.y) / zoom;
        const gridPos = isoToGrid(mouseX, mouseY, 0, 0);

        if (
          gridPos.col < 0 ||
          gridPos.col >= layout.gridWidth ||
          gridPos.row < 0 ||
          gridPos.row >= layout.gridHeight
        ) {
          setHoverGrid(null);
          return;
        }

        let dragObj: any = null;
        try {
          const rawData = e.dataTransfer.getData('application/json');
          if (rawData) dragObj = JSON.parse(rawData);
        } catch {}

        const plainText = e.dataTransfer.getData('text/plain');

        if (
          dragObj?.kind === 'furniture_catalog' ||
          FURNITURE_CATALOG.some((f) => f.type === plainText || f.type === dragObj?.furnitureType)
        ) {
          const fType = (dragObj?.furnitureType || plainText) as FurnitureType;
          if (onDropFurniture) {
            onDropFurniture(fType, gridPos.col, gridPos.row);
          } else if (onTileClick) {
            onTileClick(gridPos.col, gridPos.row);
          }
        } else if (
          dragObj?.kind === 'agent' ||
          layout.agents.some((a) => a.id === plainText || a.id === dragObj?.agentId)
        ) {
          const agentId = dragObj?.agentId || plainText;
          if (onDropAgent) {
            onDropAgent(agentId, gridPos.col, gridPos.row);
          } else {
            const agent = layout.agents.find((a) => a.id === agentId);
            if (agent && onAgentSelect) {
              onAgentSelect(agent);
            }
            if (onTileClick) {
              onTileClick(gridPos.col, gridPos.row);
            }
          }
        }

        setHoverGrid(null);
      }}
    >
      <canvas ref={canvasRef} className="twin-canvas-element" />

      {/* === CONTROLES FLOTANTES SUPERIOR-IZQUIERDA: RELOJ Y SELECTOR DE UBICACIÓN === */}
      {locationSelector && <div className="twin-canvas-location-selector-wrap">{locationSelector}</div>}

      {/* === CONTROLES FLOTANTES SUPERIOR-DERECHA: HUD DE SALUD DEL SISTEMA — SOLO EN SIMULACIÓN === */}
      {isSimulating && topRightOverlay && <div className="twin-canvas-top-right-overlay-wrap">{topRightOverlay}</div>}

      {/* === BOTÓN DE ESTADO DEL SIMULADOR (INFERIOR IZQUIERDA) — SOLO EN SIMULACIÓN === */}
      {isSimulating && (
        <div className="twin-canvas-status-btn-wrap">
          {(() => {
            if (isTimeMachineReplay) {
              return (
                <button
                  type="button"
                  onClick={onGoLiveTimeMachine}
                  className="twin-canvas-status-btn twin-canvas-status-btn--replay"
                  title="Replay Mode: Viewing historical recording. Click to return to real-time (LIVE)"
                >
                  <span className="twin-canvas-status-dot twin-canvas-status-dot--replay" />
                  <span>REPLAY</span>
                </button>
              );
            }

            if (isSimulationPlaying === false) {
              return (
                <button
                  type="button"
                  onClick={onToggleSimulationPlayPause}
                  className="twin-canvas-status-btn twin-canvas-status-btn--paused"
                  title="Simulation Paused. Click to resume"
                >
                  <span className="twin-canvas-status-dot twin-canvas-status-dot--paused" />
                  <span>PAUSED</span>
                </button>
              );
            }

            return (
              <button
                type="button"
                onClick={onToggleSimulationPlayPause}
                className="twin-canvas-status-btn twin-canvas-status-btn--live"
                title="Live System (Real-Time). Click to pause simulation"
              >
                <span className="twin-canvas-status-dot-wrap">
                  <span className="twin-canvas-status-ping" />
                  <span className="twin-canvas-status-dot twin-canvas-status-dot--live" />
                </span>
                <span>LIVE</span>
              </button>
            );
          })()}
        </div>
      )}

      {/* === CONTROLES FLOTANTES DE AYUDA (INFERIOR DERECHA) === */}
      <div className="twin-canvas-controls-wrap">
        <div className="twin-canvas-controls-bar">
          {onOpenSnapshotManager && isSimulating && (
            <>
              <button type="button" onClick={onOpenSnapshotManager} className="twin-canvas-control-btn twin-canvas-control-btn--cyan-active" title="Take System JSON Snapshot / State History" aria-label="Take JSON Snapshot">
                <Camera className="twin-icon-xs" />
              </button>
              <div className="twin-canvas-control-divider" />
            </>
          )}

          {onOpenTimeMachine && isSimulating && (
            <>
              <button
                type="button"
                onClick={onOpenTimeMachine}
                className={`twin-canvas-control-btn ${isTimeMachineReplay ? 'twin-canvas-control-btn--amber-active' : 'twin-canvas-control-btn--purple'}`}
                title={isTimeMachineReplay ? `Time Machine DVR in Replay (${timeMachineClockTime || 'Replay'}). Click to open panel.` : 'Time Machine DVR: Timeline, incident diagnostics, and historical replay'}
                aria-label="Open Time Machine DVR"
              >
                <History className="twin-icon-xs" />
              </button>
              <div className="twin-canvas-control-divider" />
            </>
          )}

          {onOpenInteractions && (
            <>
              <button type="button" onClick={onOpenInteractions} className="twin-canvas-control-btn twin-canvas-control-btn--amber" title="Interactions Manager and Guide" aria-label="Interactions Manager">
                <Zap className="twin-icon-xs" />
              </button>
              <div className="twin-canvas-control-divider" />
            </>
          )}

          {onUndo && (
            <>
              <button
                type="button"
                onClick={onUndo}
                disabled={!canUndo}
                className={`twin-canvas-control-btn ${canUndo ? 'twin-canvas-control-btn--cyan' : 'twin-canvas-control-btn--disabled'}`}
                title={isSimulating ? (canUndo ? 'Undo last simulation action (Ctrl+Z)' : 'Undo (No previous actions in simulation)') : canUndo ? 'Undo last design action (Ctrl+Z)' : 'Undo (No previous actions in layout)'}
                aria-label="Undo"
              >
                <Undo2 className="twin-icon-xs" />
              </button>
              <div className="twin-canvas-control-divider" />
            </>
          )}

          <button type="button" onClick={() => setZoom((z) => Math.min(z + 0.15, 3.0))} className="twin-canvas-control-btn twin-canvas-control-btn--cyan" title="Zoom In (+)" aria-label="Zoom In">
            <ZoomIn className="twin-icon-xs" />
          </button>

          <span className="twin-canvas-zoom-label">{Math.round(zoom * 100)}%</span>

          <button type="button" onClick={() => setZoom((z) => Math.max(z - 0.15, 0.35))} className="twin-canvas-control-btn twin-canvas-control-btn--cyan" title="Zoom Out (-)" aria-label="Zoom Out">
            <ZoomOut className="twin-icon-xs" />
          </button>

          <div className="twin-canvas-control-divider" />

          <button
            type="button"
            onClick={() => {
              if (containerRef.current) {
                setPan({
                  x: containerRef.current.clientWidth / 2,
                  y: containerRef.current.clientHeight / 4,
                });
                setZoom(1.1);
              }
            }}
            className="twin-canvas-control-btn twin-canvas-control-btn--cyan"
            title="Center View"
            aria-label="Center View"
          >
            <Focus className="twin-icon-xs" />
          </button>

          {onResetFloor && (
            <>
              <div className="twin-canvas-control-divider" />
              <button type="button" onClick={onResetFloor} className="twin-canvas-control-btn twin-canvas-control-btn--rose" title="Reset Floor (Clear all grid items and floors)" aria-label="Reset Floor">
                <Trash2 className="twin-icon-xs" />
              </button>
            </>
          )}
        </div>
      </div>

      {/* === HUD DINÁMICO PARA REDIMENSIONAR VÉRTICES DE ZONA === */}
      {!isSimulating &&
        (hoveredVertex || resizingRoom) &&
        (() => {
          const conflictRoom = resizingRoom
            ? findOverlappingRoom(
                resizingRoom.currentRoom,
                layout.rooms?.filter((r) => r.id !== resizingRoom.roomId)
              )
            : null;

          return (
            <div className={`twin-canvas-vertex-hud ${conflictRoom ? 'twin-canvas-vertex-hud--conflict' : 'twin-canvas-vertex-hud--normal'}`}>
              <span className={`twin-canvas-vertex-hud-dot ${conflictRoom ? 'twin-canvas-vertex-hud-dot--conflict' : 'twin-canvas-vertex-hud-dot--normal'}`} />
              <div className="twin-canvas-vertex-hud-text">
                {resizingRoom ? (
                  conflictRoom ? (
                    <>
                      <span className="twin-canvas-vertex-hud-title twin-canvas-vertex-hud-title--conflict">🚨 Overlap not allowed!</span>
                      <span className="twin-canvas-vertex-hud-sub twin-canvas-vertex-hud-sub--conflict">
                        Collides with zone <b>{conflictRoom.name}</b>
                      </span>
                    </>
                  ) : (
                    <>
                      <span className="twin-canvas-vertex-hud-title">📐 Resizing {resizingRoom.currentRoom.name}</span>
                      <span className="twin-canvas-vertex-hud-sub">
                        Dimensions: <b>{resizingRoom.currentRoom.width}m</b> (width) x <b>{resizingRoom.currentRoom.height}m</b> (length) &bull; {resizingRoom.currentRoom.width * resizingRoom.currentRoom.height} m²
                      </span>
                    </>
                  )
                ) : (
                  <>
                    <span className="twin-canvas-vertex-hud-title-white">{hoveredVertex?.label || 'Zone Vertex'}</span>
                    <span className="twin-canvas-vertex-hud-sub-cyan">
                      Click and drag to resize <b>{hoveredVertex?.room.name}</b>
                    </span>
                  </>
                )}
              </div>
            </div>
          );
        })()}

      {/* === MENÚ CONTEXTUAL DE AGENTE (CLIC DERECHO EN SIMULACIÓN) === */}
      {contextMenu && (
        <AgentContextMenu
          agent={layout.agents.find((a) => a.id === contextMenu.agent.id) || contextMenu.agent}
          allAgents={layout.agents}
          x={contextMenu.x}
          y={contextMenu.y}
          rooms={layout.rooms}
          onClose={() => setContextMenu(null)}
          onDynamicAction={(actionType) => {
            if (onDynamicAgentAction) {
              onDynamicAgentAction(contextMenu.agent.id, actionType);
            }
          }}
          onMoveToRoom={(agentId, room) => {
            if (onMoveToRoom) {
              onMoveToRoom(agentId, room);
            }
          }}
          onOpenModal={(initialTab) => {
            const liveAgent = layout.agents.find((a) => a.id === contextMenu.agent.id) || contextMenu.agent;
            if (onAgentSelect) {
              onAgentSelect(liveAgent, initialTab);
            }
          }}
          onFocusCamera={() => {
            const liveAgent = layout.agents.find((a) => a.id === contextMenu.agent.id) || contextMenu.agent;
            handleFocusCameraOnAgent(liveAgent);
          }}
        />
      )}

      {/* === MENÚ CONTEXTUAL DE BALDOSA (CLIC DERECHO) === */}
      {tileContextMenu && (
        <TileContextMenu
          col={tileContextMenu.col}
          row={tileContextMenu.row}
          x={tileContextMenu.x}
          y={tileContextMenu.y}
          layout={layout}
          floorMaterials={floorMaterials}
          onClose={() => setTileContextMenu(null)}
          onClearTile={(c, r) => {
            if (onClearTile) {
              onClearTile(c, r);
            } else if (onPaintTile) {
              onPaintTile(c, r, true);
            }
          }}
          onClearTileFurniture={onClearTileFurniture}
          onClearTileFloor={onClearTileFloor}
          onClearTileAgent={onClearTileAgent}
          onSetTileFloor={onSetTileFloor}
          onPlaceFurniture={(fType, c, r) => {
            if (onDropFurniture) {
              onDropFurniture(fType, c, r);
            }
          }}
          onMoveAgentToTile={(agentId, c, r) => {
            if (onDropAgent) {
              onDropAgent(agentId, c, r);
            }
          }}
          onSelectRoom={(roomId) => {
            if (onRoomSelect) onRoomSelect(roomId);
          }}
          onDeleteRoom={(roomId) => {
            if (onDeleteRoom) onDeleteRoom(roomId);
          }}
          onCreateRoom={onCreateRoom}
          onStartRoomDraft={(c, r) => {
            if (onStartRoomDraft) onStartRoomDraft(c, r);
          }}
          onSelectAgent={(agent) => {
            if (onAgentSelect) onAgentSelect(agent);
          }}
          onSelectItem={(item) => {
            if (onItemSelect) onItemSelect(item);
          }}
        />
      )}
    </div>
  );
};

// ============================================================================
// === FUNCIONES DE DIBUJO EN CANVAS 2D (SIN TAILWIND — NO REQUIEREN CSS) ===
// ============================================================================

function drawFoundationSlabEdge(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  col: number,
  row: number,
  gridWidth: number,
  gridHeight: number,
  customFloors?: Record<string, any>
) {
  const hw = TILE_WIDTH / 2;
  const hh = TILE_HEIGHT / 2;
  const slabH = 24;

  const hasCustom = customFloors && Object.keys(customFloors).length > 0;
  const isTileBuilt = (c: number, r: number) => {
    if (c < 0 || c >= gridWidth || r < 0 || r >= gridHeight) return false;
    if (hasCustom) return !!customFloors[`${c},${r}`];
    return true;
  };

  const isCurrentBuilt = isTileBuilt(col, row);
  if (!isCurrentBuilt) return;

  const isSWExposed = !isTileBuilt(col, row + 1);
  const isSEExposed = !isTileBuilt(col + 1, row);
  const isNWExposed = !isTileBuilt(col - 1, row);
  const isNEExposed = !isTileBuilt(col, row - 1);

  if (isSWExposed) {
    const grad = ctx.createLinearGradient(x - hw, y + hh, x, y + TILE_HEIGHT + slabH);
    grad.addColorStop(0, '#0d1527');
    grad.addColorStop(0.3, '#131e36');
    grad.addColorStop(0.8, '#0a101f');
    grad.addColorStop(1, '#050914');
    ctx.fillStyle = grad;

    ctx.beginPath();
    ctx.moveTo(x - hw, y + hh);
    ctx.lineTo(x, y + TILE_HEIGHT);
    ctx.lineTo(x, y + TILE_HEIGHT + slabH);
    ctx.lineTo(x - hw, y + hh + slabH);
    ctx.closePath();
    ctx.fill();

    ctx.fillStyle = '#1e293b';
    ctx.beginPath();
    ctx.moveTo(x - hw, y + hh);
    ctx.lineTo(x, y + TILE_HEIGHT);
    ctx.lineTo(x, y + TILE_HEIGHT + 3);
    ctx.lineTo(x - hw, y + hh + 3);
    ctx.closePath();
    ctx.fill();

    ctx.strokeStyle = '#040812';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(x - hw, y + hh + 8);
    ctx.lineTo(x, y + TILE_HEIGHT + 8);
    ctx.stroke();

    ctx.strokeStyle = '#38bdf8';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(x - hw, y + hh);
    ctx.lineTo(x, y + TILE_HEIGHT);
    ctx.stroke();

    ctx.strokeStyle = '#020617';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(x - hw, y + hh + slabH);
    ctx.lineTo(x, y + TILE_HEIGHT + slabH);
    ctx.stroke();
  }

  if (isSEExposed) {
    const grad = ctx.createLinearGradient(x, y + TILE_HEIGHT, x + hw, y + hh + slabH);
    grad.addColorStop(0, '#1c2b47');
    grad.addColorStop(0.3, '#25385a');
    grad.addColorStop(0.8, '#142036');
    grad.addColorStop(1, '#09101d');
    ctx.fillStyle = grad;

    ctx.beginPath();
    ctx.moveTo(x, y + TILE_HEIGHT);
    ctx.lineTo(x + hw, y + hh);
    ctx.lineTo(x + hw, y + hh + slabH);
    ctx.lineTo(x, y + TILE_HEIGHT + slabH);
    ctx.closePath();
    ctx.fill();

    ctx.fillStyle = '#334155';
    ctx.beginPath();
    ctx.moveTo(x, y + TILE_HEIGHT);
    ctx.lineTo(x + hw, y + hh);
    ctx.lineTo(x + hw, y + hh + 3);
    ctx.lineTo(x, y + TILE_HEIGHT + 3);
    ctx.closePath();
    ctx.fill();

    ctx.strokeStyle = '#0a101f';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(x, y + TILE_HEIGHT + 8);
    ctx.lineTo(x + hw, y + hh + 8);
    ctx.stroke();

    ctx.strokeStyle = '#60a5fa';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(x, y + TILE_HEIGHT);
    ctx.lineTo(x + hw, y + hh);
    ctx.stroke();

    ctx.strokeStyle = '#050914';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(x, y + TILE_HEIGHT + slabH);
    ctx.lineTo(x + hw, y + hh + slabH);
    ctx.stroke();
  }

  if (isNWExposed) {
    const grad = ctx.createLinearGradient(x - hw, y + hh, x, y + slabH);
    grad.addColorStop(0, '#090d1a');
    grad.addColorStop(1, '#03060d');
    ctx.fillStyle = grad;

    ctx.beginPath();
    ctx.moveTo(x - hw, y + hh);
    ctx.lineTo(x, y);
    ctx.lineTo(x, y + slabH);
    ctx.lineTo(x - hw, y + hh + slabH);
    ctx.closePath();
    ctx.fill();

    ctx.strokeStyle = '#1e293b';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x - hw, y + hh);
    ctx.stroke();
  }

  if (isNEExposed) {
    const grad = ctx.createLinearGradient(x, y, x + hw, y + hh + slabH);
    grad.addColorStop(0, '#0f172a');
    grad.addColorStop(1, '#070b14');
    ctx.fillStyle = grad;

    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x + hw, y + hh);
    ctx.lineTo(x + hw, y + hh + slabH);
    ctx.lineTo(x, y + slabH);
    ctx.closePath();
    ctx.fill();

    ctx.strokeStyle = '#334155';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(x + hw, y + hh);
    ctx.lineTo(x, y);
    ctx.stroke();
  }
}

function drawBaseBoardTile(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  col: number,
  row: number,
  gridWidth: number,
  gridHeight: number,
  customFloors?: Record<string, any>,
  themeConfig?: ReturnType<typeof getThemeConfig>
) {
  const hw = TILE_WIDTH / 2;
  const hh = TILE_HEIGHT / 2;

  drawFoundationSlabEdge(ctx, x, y, col, row, gridWidth, gridHeight, customFloors);

  ctx.beginPath();
  ctx.moveTo(x, y);
  ctx.lineTo(x + hw, y + hh);
  ctx.lineTo(x, y + TILE_HEIGHT);
  ctx.lineTo(x - hw, y + hh);
  ctx.closePath();

  const isEven = (col + row) % 2 === 0;
  const evenColor = themeConfig?.styles.canvasBaseTileEven || '#0c1322';
  const oddColor = themeConfig?.styles.canvasBaseTileOdd || '#080d18';
  ctx.fillStyle = isEven ? evenColor : oddColor;
  ctx.fill();

  ctx.strokeStyle = themeConfig?.styles.canvasGridLine || '#182234';
  ctx.lineWidth = 1;
  ctx.stroke();

  const isBottomCorner = col === gridWidth - 1 && row === gridHeight - 1;

  if (!isBottomCorner) {
    ctx.fillStyle = themeConfig?.styles.canvasHighlight || 'rgba(56, 189, 248, 0.2)';
    ctx.fillRect(Math.round(x - 1), Math.round(y + hh - 1), 2, 2);
  }

  if (isBottomCorner) {
    ctx.fillStyle = themeConfig?.styles.primaryLight || '#475569';
    ctx.font = 'bold 8px monospace';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(`${gridWidth}x${gridHeight}`, x, y + hh);
  }
}

function drawFloorTile(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  col: number,
  row: number,
  type: string,
  roomName?: string,
  customImage?: string,
  gridWidth: number = 12,
  gridHeight: number = 12,
  customFloors?: Record<string, any>,
  materialDef?: FloorMaterialOption,
  customImageSecondary?: string
) {
  const hw = TILE_WIDTH / 2;
  const hh = TILE_HEIGHT / 2;

  drawFoundationSlabEdge(ctx, x, y, col, row, gridWidth, gridHeight, customFloors);

  ctx.beginPath();
  ctx.moveTo(x, y);
  ctx.lineTo(x + hw, y + hh);
  ctx.lineTo(x, y + TILE_HEIGHT);
  ctx.lineTo(x - hw, y + hh);
  ctx.closePath();

  const isEven = (col + row) % 2 === 0;
  const hasChecker = materialDef ? materialDef.hasCheckerboard !== false : true;

  const imgAUrl = materialDef?.customImage || customImage;
  const imgBUrl = materialDef?.customImageSecondary || customImageSecondary;

  if (imgAUrl) {
    if (hasChecker) {
      if (imgBUrl) {
        const targetUrl = isEven ? imgAUrl : imgBUrl;
        const img = getOrLoadCustomImage(targetUrl);
        if (img && img.complete && img.naturalWidth > 0) {
          ctx.save();
          ctx.clip();
          ctx.drawImage(img, x - hw, y, TILE_WIDTH, TILE_HEIGHT);
          ctx.restore();

          ctx.strokeStyle = 'rgba(255, 255, 255, 0.25)';
          ctx.lineWidth = 1;
          ctx.stroke();

          if (roomName && col % 4 === 0 && row % 4 === 0) {
            ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
            ctx.strokeStyle = '#0f172a';
            ctx.lineWidth = 2;
            ctx.font = 'bold 8px sans-serif';
            ctx.textAlign = 'center';
            ctx.strokeText(roomName, x, y + hh);
            ctx.fillText(roomName, x, y + hh);
          }
          return;
        }
      } else {
        const img = getOrLoadCustomImage(imgAUrl);
        if (img && img.complete && img.naturalWidth > 0) {
          ctx.save();
          ctx.clip();
          ctx.drawImage(img, x - hw, y, TILE_WIDTH, TILE_HEIGHT);

          if (!isEven) {
            const brightness = materialDef?.checkerBrightness ?? 0.78;
            const alpha = Math.min(0.65, Math.abs(1 - brightness));
            const isDarken = brightness <= 1;
            ctx.fillStyle = isDarken ? `rgba(0, 0, 0, ${alpha})` : `rgba(255, 255, 255, ${alpha})`;
            ctx.fill();
          }
          ctx.restore();

          ctx.strokeStyle = 'rgba(255, 255, 255, 0.25)';
          ctx.lineWidth = 1;
          ctx.stroke();

          if (roomName && col % 4 === 0 && row % 4 === 0) {
            ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
            ctx.strokeStyle = '#0f172a';
            ctx.lineWidth = 2;
            ctx.font = 'bold 8px sans-serif';
            ctx.textAlign = 'center';
            ctx.strokeText(roomName, x, y + hh);
            ctx.fillText(roomName, x, y + hh);
          }
          return;
        }
      }
    } else {
      const img = getOrLoadCustomImage(imgAUrl);
      if (img && img.complete && img.naturalWidth > 0) {
        ctx.save();
        ctx.clip();
        ctx.drawImage(img, x - hw, y, TILE_WIDTH, TILE_HEIGHT);
        ctx.restore();

        ctx.strokeStyle = 'rgba(255, 255, 255, 0.25)';
        ctx.lineWidth = 1;
        ctx.stroke();

        if (roomName && col % 4 === 0 && row % 4 === 0) {
          ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
          ctx.strokeStyle = '#0f172a';
          ctx.lineWidth = 2;
          ctx.font = 'bold 8px sans-serif';
          ctx.textAlign = 'center';
          ctx.strokeText(roomName, x, y + hh);
          ctx.fillText(roomName, x, y + hh);
        }
        return;
      }
    }
  }

  if (type === 'blue_checkered') {
    ctx.fillStyle = hasChecker ? (isEven ? '#0284c7' : '#0369a1') : '#0284c7';
    ctx.fill();

    ctx.strokeStyle = hasChecker ? (isEven ? '#38bdf8' : '#0284c7') : '#38bdf8';
    ctx.lineWidth = 1;
    ctx.stroke();

    if (!hasChecker || isEven) {
      ctx.fillStyle = isEven ? '#e0f2fe' : '#7dd3fc';
      ctx.fillRect(Math.round(x - 2), Math.round(y + 3), 4, 2);
    }
  } else if (type === 'wood_parquet') {
    ctx.fillStyle = hasChecker ? (isEven ? '#854d0e' : '#713f12') : '#854d0e';
    ctx.fill();

    ctx.strokeStyle = 'rgba(0, 0, 0, 0.25)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(x - hw + 6, y + hh - 3);
    ctx.lineTo(x + hw - 6, y + hh - 3);
    ctx.moveTo(x - hw + 10, y + hh + 3);
    ctx.lineTo(x + hw - 10, y + hh + 3);
    ctx.stroke();

    ctx.strokeStyle = '#a16207';
    ctx.stroke();
  } else if (type === 'grey_carpet') {
    ctx.fillStyle = hasChecker ? (isEven ? '#334155' : '#1e293b') : '#334155';
    ctx.fill();

    ctx.fillStyle = hasChecker ? (isEven ? '#475569' : '#1e293b') : '#475569';
    ctx.fillRect(Math.round(x - 6), Math.round(y + hh - 2), 2, 2);
    ctx.fillRect(Math.round(x + 4), Math.round(y + hh + 2), 2, 2);
    ctx.fillRect(Math.round(x - 2), Math.round(y + hh + 4), 2, 2);

    ctx.strokeStyle = '#1e293b';
    ctx.lineWidth = 1;
    ctx.stroke();
  } else if (type === 'bathroom_tile') {
    ctx.fillStyle = hasChecker ? (isEven ? '#06b6d4' : '#0891b2') : '#06b6d4';
    ctx.fill();

    if (!hasChecker || isEven) {
      ctx.fillStyle = '#cffafe';
      ctx.fillRect(Math.round(x - 3), Math.round(y + 4), 6, 2);
    }

    ctx.strokeStyle = '#164e63';
    ctx.lineWidth = 1;
    ctx.stroke();
  } else if (type === 'breakroom_tile') {
    ctx.fillStyle = hasChecker ? (isEven ? '#65a30d' : '#4d7c0f') : '#65a30d';
    ctx.fill();

    ctx.strokeStyle = '#365314';
    ctx.lineWidth = 1;
    ctx.stroke();
  } else if (type === 'terracotta') {
    ctx.fillStyle = hasChecker ? (isEven ? '#c2410c' : '#9a3412') : '#c2410c';
    ctx.fill();

    ctx.strokeStyle = '#431407';
    ctx.lineWidth = 1;
    ctx.stroke();
  } else if (type === 'marble_luxury') {
    ctx.fillStyle = hasChecker ? (isEven ? '#e2e8f0' : '#cbd5e1') : '#e2e8f0';
    ctx.fill();

    ctx.strokeStyle = 'rgba(100, 116, 139, 0.45)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(x - hw + 6, y + hh - 3);
    ctx.lineTo(x + 2, y + hh + 2);
    ctx.lineTo(x + hw - 4, y + hh - 1);
    ctx.stroke();

    ctx.strokeStyle = '#94a3b8';
    ctx.lineWidth = 1;
    ctx.stroke();
  } else if (type === 'cyber_deck') {
    ctx.fillStyle = hasChecker ? (isEven ? '#1e1b4b' : '#0f172a') : '#1e1b4b';
    ctx.fill();

    ctx.strokeStyle = '#6366f1';
    ctx.lineWidth = 1;
    ctx.stroke();

    ctx.fillStyle = isEven ? '#818cf8' : '#4f46e5';
    ctx.fillRect(Math.round(x - 2), Math.round(y + hh - 2), 4, 3);
  } else {
    const baseColor = materialDef?.previewColor ? resolveColorToHex(materialDef.previewColor) : '#1e293b';
    const tones = getCheckerboardColorTones(
      baseColor,
      materialDef?.secondaryColor,
      hasChecker,
      materialDef?.checkerBrightness !== undefined ? Math.round((materialDef.checkerBrightness - 1) * 100) : -18
    );

    ctx.fillStyle = isEven ? tones.primary : tones.secondary;
    ctx.fill();

    ctx.strokeStyle = isEven ? tones.highlight : tones.shadow;
    ctx.lineWidth = 1;
    ctx.stroke();

    if (hasChecker && isEven) {
      ctx.fillStyle = tones.highlight;
      ctx.fillRect(Math.round(x - 1), Math.round(y + 2), 2, 1);
    }
  }

  if (roomName && col % 3 === 0 && row % 3 === 0) {
    ctx.fillStyle = 'rgba(15, 23, 42, 0.75)';
    ctx.fillRect(Math.round(x - 28), Math.round(y + hh - 6), 56, 12);
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.2)';
    ctx.strokeRect(Math.round(x - 28), Math.round(y + hh - 6), 56, 12);

    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 8px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(roomName, x, y + hh + 3);
  }
}

function drawTileHighlight(ctx: CanvasRenderingContext2D, x: number, y: number, color: string, alpha: number) {
  const hw = TILE_WIDTH / 2;
  const hh = TILE_HEIGHT / 2;

  ctx.save();
  ctx.beginPath();
  ctx.moveTo(x, y);
  ctx.lineTo(x + hw, y + hh);
  ctx.lineTo(x, y + TILE_HEIGHT);
  ctx.lineTo(x - hw, y + hh);
  ctx.closePath();

  ctx.fillStyle = color;
  ctx.globalAlpha = alpha;
  ctx.fill();

  ctx.strokeStyle = color;
  ctx.lineWidth = 2;
  ctx.globalAlpha = 1;
  ctx.stroke();
  ctx.restore();
}

const assetImageCache = new Map<string, HTMLImageElement>();

function getOrLoadCustomImage(url: string, onLoaded?: () => void): HTMLImageElement | null {
  if (!url) return null;
  const cached = assetImageCache.get(url);
  if (cached) {
    if (cached.complete && cached.naturalWidth !== 0) {
      return cached;
    }
    return null;
  }
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.src = url;
  img.onload = () => {
    if (onLoaded) onLoaded();
  };
  assetImageCache.set(url, img);
  return null;
}

// --- DETECCIÓN DE OCLUSIÓN / TRANSLUCIDEZ PARA BALDOSAS EN HOVER ---

function isItemOccludingHover(
  item: PlacedFurniture,
  hoverGrid: { col: number; row: number } | null,
  originX: number,
  originY: number
): boolean {
  if (!hoverGrid) return false;

  const def = FURNITURE_CATALOG.find((f) => f.type === item.type);
  const itemW = item.width || def?.width || 1;
  const itemH = item.height || def?.height || 1;

  if (
    hoverGrid.col >= item.col &&
    hoverGrid.col < item.col + itemW &&
    hoverGrid.row >= item.row &&
    hoverGrid.row < item.row + itemH
  ) {
    return false;
  }

  const itemCenterCol = item.col + (itemW - 1) / 2;
  const itemCenterRow = item.row + (itemH - 1) / 2;

  const itemMinDepth = item.col + item.row;
  const hoverDepth = hoverGrid.col + hoverGrid.row;

  if (itemMinDepth >= hoverDepth) {
    const hoverIso = gridToIso(hoverGrid.col, hoverGrid.row, 0, originX, originY);
    const itemIso = gridToIso(itemCenterCol, itemCenterRow, 0, originX, originY);

    let visualHeight = 35;
    if (def) {
      if (def.type.startsWith('wall_') || def.type === 'door_single') {
        visualHeight = 60;
      } else if (
        ['filing_cabinet', 'fridge', 'vending_machine', 'water_dispenser', 'coffee_station', 'whiteboard', 'tv_screen', 'plant_monstera'].indexOf(def.type) !== -1
      ) {
        visualHeight = 48;
      } else if (
        ['cubicle_double', 'executive_desk', 'desk_single', 'meeting_table', 'printer_large', 'snack_table'].indexOf(def.type) !== -1
      ) {
        visualHeight = 36;
      } else {
        visualHeight = 28;
      }
    }

    if (item.customImage || def?.customImage) {
      const renderW = Math.max(32, Math.max(itemW, itemH) * TILE_WIDTH * 0.85);
      visualHeight = Math.max(visualHeight, renderW * 1.1);
    }

    const dy = itemIso.y - hoverIso.y;
    const dx = Math.abs(itemIso.x - hoverIso.x);

    const maxHorizRadius = (Math.max(itemW, itemH) * TILE_WIDTH) / 2 + 12;

    if (dy >= 5 && dy <= visualHeight + 15 && dx <= maxHorizRadius) {
      return true;
    }
  }

  return false;
}

function isAgentOccludingHover(
  agent: AIAgent,
  hoverGrid: { col: number; row: number } | null,
  originX: number,
  originY: number
): boolean {
  if (!hoverGrid) return false;
  const agentCol = Math.round(agent.currentCol);
  const agentRow = Math.round(agent.currentRow);

  if (agentCol === hoverGrid.col && agentRow === hoverGrid.row) {
    return false;
  }

  const agentDepth = agentCol + agentRow;
  const hoverDepth = hoverGrid.col + hoverGrid.row;

  if (agentDepth >= hoverDepth) {
    const hoverIso = gridToIso(hoverGrid.col, hoverGrid.row, 0, originX, originY);
    const agentIso = gridToIso(agent.currentCol, agent.currentRow, 0, originX, originY);

    const dy = agentIso.y - hoverIso.y;
    const dx = Math.abs(agentIso.x - hoverIso.x);

    if (dy >= 5 && dy <= 64 && dx <= 28) {
      return true;
    }
  }

  return false;
}

function drawFurnitureItem(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  item: PlacedFurniture,
  isSelected: boolean,
  isOccludingHover: boolean = false
) {
  const def = FURNITURE_CATALOG.find((f) => f.type === item.type);
  if (!def) return;

  const itemW = item.width || def.width || 1;
  const itemH = item.height || def.height || 1;

  ctx.save();

  if (isSelected) {
    for (let c = 0; c < itemW; c++) {
      for (let r = 0; r < itemH; r++) {
        const { x: tx, y: ty } = gridToIso(item.col + c, item.row + r, 0, 0, 0);
        drawTileHighlight(ctx, tx, ty, '#f59e0b', 0.65);
      }
    }
  }

  if (isOccludingHover) {
    ctx.globalAlpha = 0.35;
  }

  const centerX = x + (itemW - 1) * (TILE_WIDTH / 4) - (itemH - 1) * (TILE_WIDTH / 4);
  const centerY = y + (itemW - 1) * (TILE_HEIGHT / 4) + (itemH - 1) * (TILE_HEIGHT / 4);

  ctx.beginPath();
  ctx.ellipse(centerX, centerY + TILE_HEIGHT / 2 + 2, 18 * Math.max(itemW, itemH), 9 * Math.max(itemW, itemH), 0, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(0, 0, 0, 0.38)';
  ctx.fill();

  const customImgUrl = item.customImage || def.customImage;
  if (customImgUrl) {
    const customImg = getOrLoadCustomImage(customImgUrl);
    if (customImg && customImg.complete && customImg.naturalWidth > 0) {
      const renderW = Math.max(32, Math.max(itemW, itemH) * TILE_WIDTH * 0.85);
      const aspectRatio = customImg.naturalHeight / customImg.naturalWidth;
      const renderH = renderW * aspectRatio;

      ctx.drawImage(customImg, centerX - renderW / 2, centerY - renderH + TILE_HEIGHT / 2, renderW, renderH);

      if (item.label) {
        ctx.fillStyle = '#ffffff';
        ctx.strokeStyle = '#0f172a';
        ctx.lineWidth = 2;
        ctx.font = 'bold 9px sans-serif';
        ctx.textAlign = 'center';
        ctx.strokeText(item.label, centerX, centerY - renderH - 4);
        ctx.fillText(item.label, centerX, centerY - renderH - 4);
      }

      ctx.restore();
      return;
    }
  }

  switch (item.type) {
    case 'desk_single':
    case 'cubicle_double':
    case 'executive_desk':
      drawPixelDeskGraphic(ctx, centerX, centerY, item.type, item.label);
      break;
    case 'wall_brick':
      drawPixelWallGraphic(ctx, centerX, centerY, item.rotation);
      break;
    case 'wall_glass':
      drawPixelGlassWallGraphic(ctx, centerX, centerY, item.rotation);
      break;
    case 'door_single':
      drawPixelDoorGraphic(ctx, centerX, centerY);
      break;
    case 'coffee_station':
      drawPixelCoffeeStationGraphic(ctx, centerX, centerY, item.label);
      break;
    case 'water_dispenser':
      drawPixelWaterDispenserGraphic(ctx, centerX, centerY, item.label);
      break;
    case 'fridge':
      drawPixelFridgeGraphic(ctx, centerX, centerY);
      break;
    case 'vending_machine':
      drawPixelVendingMachineGraphic(ctx, centerX, centerY);
      break;
    case 'snack_table':
      drawPixelSnackTableGraphic(ctx, centerX, centerY);
      break;
    case 'sofa':
      drawPixelSofaGraphic(ctx, centerX, centerY);
      break;
    case 'toilet':
      drawPixelToiletGraphic(ctx, centerX, centerY, item.label);
      break;
    case 'sink':
      drawPixelSinkGraphic(ctx, centerX, centerY);
      break;
    case 'whiteboard':
      drawPixelWhiteboardGraphic(ctx, centerX, centerY, item.label);
      break;
    case 'meeting_table':
      drawPixelMeetingTableGraphic(ctx, centerX, centerY);
      break;
    case 'tv_screen':
      drawPixelTVScreenGraphic(ctx, centerX, centerY);
      break;
    case 'printer_large':
      drawPixelPrinterGraphic(ctx, centerX, centerY);
      break;
    case 'filing_cabinet':
      drawPixelFilingCabinetGraphic(ctx, centerX, centerY);
      break;
    case 'plant_monstera':
      drawPixelPlantGraphic(ctx, centerX, centerY);
      break;
    case 'air_conditioner':
      drawPixelACGraphic(ctx, centerX, centerY);
      break;
    case 'robot_vacuum':
      drawPixelVacuumGraphic(ctx, centerX, centerY);
      break;
    default:
      drawPixelGenericGraphic(ctx, centerX, centerY, def.color, def.icon, item.label || def.name);
      break;
  }

  ctx.restore();
}

// --- FUNCIONES DE DIBUJO PIXEL ART DE ALTA CALIDAD ---

function drawPixelDeskGraphic(ctx: CanvasRenderingContext2D, x: number, y: number, type: string, label?: string) {
  const isExec = type === 'executive_desk';
  const isCubicle = type === 'cubicle_double';

  const deskH = 20;
  const topY = y - deskH;

  if (isCubicle) {
    ctx.fillStyle = '#b45309';
    ctx.fillRect(x - 22, topY - 18, 44, 20);
    ctx.fillStyle = '#f59e0b';
    ctx.fillRect(x - 20, topY - 16, 40, 16);

    ctx.fillStyle = '#fef08a';
    ctx.fillRect(x - 14, topY - 12, 6, 6);
    ctx.fillStyle = '#ec4899';
    ctx.fillRect(x - 4, topY - 14, 6, 6);
    ctx.fillStyle = '#38bdf8';
    ctx.fillRect(x + 8, topY - 12, 6, 6);

    ctx.strokeStyle = '#78350f';
    ctx.lineWidth = 1;
    ctx.strokeRect(x - 22, topY - 18, 44, 20);
  }

  ctx.fillStyle = '#1e293b';
  ctx.fillRect(x - 18, topY + 8, 4, 12);
  ctx.fillRect(x + 14, topY + 8, 4, 12);

  ctx.fillStyle = '#475569';
  ctx.fillRect(x + 4, topY + 4, 12, 14);
  ctx.fillStyle = '#cbd5e1';
  ctx.fillRect(x + 6, topY + 6, 8, 3);
  ctx.fillRect(x + 6, topY + 11, 8, 5);

  ctx.fillStyle = isExec ? '#78350f' : isCubicle ? '#d97706' : '#b45309';
  ctx.beginPath();
  ctx.moveTo(x, topY - 10);
  ctx.lineTo(x + 24, topY + 2);
  ctx.lineTo(x, topY + 14);
  ctx.lineTo(x - 24, topY + 2);
  ctx.closePath();
  ctx.fill();

  ctx.strokeStyle = isExec ? '#f59e0b' : '#fde047';
  ctx.lineWidth = 1;
  ctx.stroke();

  ctx.fillStyle = '#0f172a';
  ctx.fillRect(x - 10, topY - 16, 3, 8);
  ctx.fillRect(x + 6, topY - 16, 3, 8);

  ctx.fillStyle = '#1e293b';
  ctx.fillRect(x - 18, topY - 22, 14, 12);
  ctx.fillStyle = '#38bdf8';
  ctx.fillRect(x - 16, topY - 20, 10, 8);
  ctx.fillStyle = '#f8fafc';
  ctx.fillRect(x - 15, topY - 18, 6, 1);
  ctx.fillRect(x - 15, topY - 16, 8, 1);
  ctx.fillRect(x - 15, topY - 14, 4, 1);

  ctx.fillStyle = '#1e293b';
  ctx.fillRect(x + 2, topY - 22, 14, 12);
  ctx.fillStyle = '#10b981';
  ctx.fillRect(x + 4, topY - 20, 10, 8);
  ctx.fillStyle = '#fef08a';
  ctx.fillRect(x + 6, topY - 15, 2, 3);
  ctx.fillRect(x + 9, topY - 17, 2, 5);

  ctx.fillStyle = '#0f172a';
  ctx.fillRect(x - 8, topY, 10, 4);
  ctx.fillStyle = '#e2e8f0';
  ctx.fillRect(x - 7, topY + 1, 8, 2);

  ctx.fillStyle = '#3b82f6';
  ctx.fillRect(x + 4, topY + 1, 4, 4);

  ctx.fillStyle = '#ef4444';
  ctx.fillRect(x - 14, topY - 2, 3, 4);

  const chairY = topY + 14;
  ctx.fillStyle = '#0f172a';
  ctx.fillRect(x - 6, chairY + 8, 12, 2);
  ctx.fillRect(x - 1, chairY + 4, 2, 5);
  ctx.fillStyle = '#334155';
  ctx.beginPath();
  ctx.ellipse(x, chairY + 2, 7, 4, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = '#1e293b';
  ctx.fillRect(x - 5, chairY - 6, 10, 8);

  if (label) {
    ctx.fillStyle = '#ffffff';
    ctx.strokeStyle = '#0f172a';
    ctx.lineWidth = 2;
    ctx.font = 'bold 9px sans-serif';
    ctx.textAlign = 'center';
    ctx.strokeText(label, x, topY - 26);
    ctx.fillText(label, x, topY - 26);
  }
}

function drawPixelWallGraphic(ctx: CanvasRenderingContext2D, x: number, y: number, rotation: number = 0) {
  drawPixelGlassWallGraphic(ctx, x, y, rotation);
}

function drawPixelGlassWallGraphic(ctx: CanvasRenderingContext2D, x: number, y: number, rotation: number = 0) {
  const wallH = 46;
  const hw = TILE_WIDTH / 2;
  const hh = TILE_HEIGHT / 2;

  const isPerpendicular = rotation === 90 || rotation === 270;

  ctx.save();

  if (!isPerpendicular) {
    const xLeft = x - hw;
    const yLeft = y + hh;
    const xRight = x;
    const yRight = y + TILE_HEIGHT;

    const thickX = 4;
    const thickY = 2;

    ctx.beginPath();
    ctx.moveTo(xLeft - thickX, yLeft - thickY);
    ctx.lineTo(xRight, yRight);
    ctx.lineTo(xRight + 2, yRight + 2);
    ctx.lineTo(xLeft - thickX - 2, yLeft - thickY + 2);
    ctx.closePath();
    ctx.fillStyle = 'rgba(15, 23, 42, 0.4)';
    ctx.fill();

    ctx.beginPath();
    ctx.moveTo(xLeft, yLeft);
    ctx.lineTo(xLeft - thickX, yLeft - thickY);
    ctx.lineTo(xLeft - thickX, yLeft - wallH - thickY);
    ctx.lineTo(xLeft, yLeft - wallH);
    ctx.closePath();
    ctx.fillStyle = '#362831';
    ctx.fill();
    ctx.strokeStyle = '#1e141a';
    ctx.lineWidth = 1;
    ctx.stroke();

    ctx.strokeStyle = '#4e3b46';
    ctx.beginPath();
    ctx.moveTo(xLeft - 1, yLeft - wallH + 1);
    ctx.lineTo(xLeft - 1, yLeft - 1);
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(xLeft, yLeft - wallH);
    ctx.lineTo(xRight, yRight - wallH);
    ctx.lineTo(xRight - thickX, yRight - wallH - thickY);
    ctx.lineTo(xLeft - thickX, yLeft - wallH - thickY);
    ctx.closePath();
    ctx.fillStyle = '#54434c';
    ctx.fill();
    ctx.strokeStyle = '#22171e';
    ctx.lineWidth = 1;
    ctx.stroke();

    ctx.strokeStyle = '#6d5865';
    ctx.beginPath();
    ctx.moveTo(xLeft - thickX + 1, yLeft - wallH - thickY + 1);
    ctx.lineTo(xRight - thickX - 1, yRight - wallH - thickY + 1);
    ctx.stroke();

    const baseH = 3;
    ctx.beginPath();
    ctx.moveTo(xLeft, yLeft);
    ctx.lineTo(xRight, yRight);
    ctx.lineTo(xRight, yRight - baseH);
    ctx.lineTo(xLeft, yLeft - baseH);
    ctx.closePath();
    ctx.fillStyle = '#2c2027';
    ctx.fill();
    ctx.strokeStyle = '#1a1217';
    ctx.stroke();

    const glassTopLeftY = yLeft - wallH + 2;
    const glassTopRightY = yRight - wallH + 2;
    const glassBotLeftY = yLeft - baseH;
    const glassBotRightY = yRight - baseH;

    const glassGrad = ctx.createLinearGradient(xLeft, glassTopLeftY, xRight, glassBotRightY);
    glassGrad.addColorStop(0.0, '#5cbaba');
    glassGrad.addColorStop(0.35, '#4ba5a5');
    glassGrad.addColorStop(0.7, '#3c9393');
    glassGrad.addColorStop(1.0, '#2d7878');

    ctx.beginPath();
    ctx.moveTo(xLeft, glassTopLeftY);
    ctx.lineTo(xRight, glassTopRightY);
    ctx.lineTo(xRight, glassBotRightY);
    ctx.lineTo(xLeft, glassBotLeftY);
    ctx.closePath();

    ctx.fillStyle = glassGrad;
    ctx.fill();

    ctx.fillStyle = 'rgba(78, 166, 166, 0.45)';
    ctx.fill();

    const numPanes = 5;
    for (let i = 1; i < numPanes; i++) {
      const t = i / numPanes;
      const gx = xLeft + t * (xRight - xLeft);
      const gyTop = glassTopLeftY + t * (glassTopRightY - glassTopLeftY);
      const gyBot = glassBotLeftY + t * (glassBotRightY - glassBotLeftY);

      ctx.strokeStyle = '#266565';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(gx, gyTop);
      ctx.lineTo(gx, gyBot);
      ctx.stroke();

      ctx.strokeStyle = '#74cccc';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(gx + 1, gyTop);
      ctx.lineTo(gx + 1, gyBot);
      ctx.stroke();
    }

    ctx.fillStyle = 'rgba(255, 255, 255, 0.12)';
    ctx.beginPath();
    ctx.moveTo(xLeft + 4, glassTopLeftY + 4);
    ctx.lineTo(xLeft + 14, glassTopLeftY + 4);
    ctx.lineTo(xRight - 6, glassBotRightY - 6);
    ctx.lineTo(xRight - 16, glassBotRightY - 6);
    ctx.closePath();
    ctx.fill();

    ctx.strokeStyle = '#1e141a';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(xLeft, yLeft);
    ctx.lineTo(xRight, yRight);
    ctx.lineTo(xRight, yRight - wallH);
    ctx.lineTo(xLeft, yLeft - wallH);
    ctx.closePath();
    ctx.stroke();
  } else {
    const xLeft = x;
    const yLeft = y + TILE_HEIGHT;
    const xRight = x + hw;
    const yRight = y + hh;

    const thickX = 4;
    const thickY = 2;

    ctx.beginPath();
    ctx.moveTo(xLeft, yLeft);
    ctx.lineTo(xRight + thickX, yRight - thickY);
    ctx.lineTo(xRight + thickX + 2, yRight - thickY + 2);
    ctx.lineTo(xLeft + 2, yLeft + 2);
    ctx.closePath();
    ctx.fillStyle = 'rgba(15, 23, 42, 0.4)';
    ctx.fill();

    ctx.beginPath();
    ctx.moveTo(xRight, yRight);
    ctx.lineTo(xRight + thickX, yRight - thickY);
    ctx.lineTo(xRight + thickX, yRight - wallH - thickY);
    ctx.lineTo(xRight, yRight - wallH);
    ctx.closePath();
    ctx.fillStyle = '#362831';
    ctx.fill();
    ctx.strokeStyle = '#1e141a';
    ctx.lineWidth = 1;
    ctx.stroke();

    ctx.strokeStyle = '#4e3b46';
    ctx.beginPath();
    ctx.moveTo(xRight + thickX - 1, yRight - wallH - thickY + 1);
    ctx.lineTo(xRight + thickX - 1, yRight - thickY - 1);
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(xLeft, yLeft - wallH);
    ctx.lineTo(xRight, yRight - wallH);
    ctx.lineTo(xRight + thickX, yRight - wallH - thickY);
    ctx.lineTo(xLeft + thickX, yLeft - wallH - thickY);
    ctx.closePath();
    ctx.fillStyle = '#54434c';
    ctx.fill();
    ctx.strokeStyle = '#22171e';
    ctx.lineWidth = 1;
    ctx.stroke();

    ctx.strokeStyle = '#6d5865';
    ctx.beginPath();
    ctx.moveTo(xLeft + thickX + 1, yLeft - wallH - thickY + 1);
    ctx.lineTo(xRight + thickX - 1, yRight - wallH - thickY + 1);
    ctx.stroke();

    const baseH = 3;
    ctx.beginPath();
    ctx.moveTo(xLeft, yLeft);
    ctx.lineTo(xRight, yRight);
    ctx.lineTo(xRight, yRight - baseH);
    ctx.lineTo(xLeft, yLeft - baseH);
    ctx.closePath();
    ctx.fillStyle = '#2c2027';
    ctx.fill();
    ctx.strokeStyle = '#1a1217';
    ctx.stroke();

    const glassTopLeftY = yLeft - wallH + 2;
    const glassTopRightY = yRight - wallH + 2;
    const glassBotLeftY = yLeft - baseH;
    const glassBotRightY = yRight - baseH;

    const glassGrad = ctx.createLinearGradient(xLeft, glassTopLeftY, xRight, glassBotRightY);
    glassGrad.addColorStop(0.0, '#5cbaba');
    glassGrad.addColorStop(0.35, '#4ba5a5');
    glassGrad.addColorStop(0.7, '#3c9393');
    glassGrad.addColorStop(1.0, '#2d7878');

    ctx.beginPath();
    ctx.moveTo(xLeft, glassTopLeftY);
    ctx.lineTo(xRight, glassTopRightY);
    ctx.lineTo(xRight, glassBotRightY);
    ctx.lineTo(xLeft, glassBotLeftY);
    ctx.closePath();

    ctx.fillStyle = glassGrad;
    ctx.fill();

    ctx.fillStyle = 'rgba(78, 166, 166, 0.45)';
    ctx.fill();

    const numPanes = 5;
    for (let i = 1; i < numPanes; i++) {
      const t = i / numPanes;
      const gx = xLeft + t * (xRight - xLeft);
      const gyTop = glassTopLeftY + t * (glassTopRightY - glassTopLeftY);
      const gyBot = glassBotLeftY + t * (glassBotRightY - glassBotLeftY);

      ctx.strokeStyle = '#266565';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(gx, gyTop);
      ctx.lineTo(gx, gyBot);
      ctx.stroke();

      ctx.strokeStyle = '#74cccc';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(gx + 1, gyTop);
      ctx.lineTo(gx + 1, gyBot);
      ctx.stroke();
    }

    ctx.fillStyle = 'rgba(255, 255, 255, 0.12)';
    ctx.beginPath();
    ctx.moveTo(xLeft + 4, glassTopLeftY + 4);
    ctx.lineTo(xLeft + 14, glassTopLeftY + 4);
    ctx.lineTo(xRight - 6, glassBotRightY - 6);
    ctx.lineTo(xRight - 16, glassBotRightY - 6);
    ctx.closePath();
    ctx.fill();

    ctx.strokeStyle = '#1e141a';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(xLeft, yLeft);
    ctx.lineTo(xRight, yRight);
    ctx.lineTo(xRight, yRight - wallH);
    ctx.lineTo(xLeft, yLeft - wallH);
    ctx.closePath();
    ctx.stroke();
  }

  ctx.restore();
}

function drawPixelDoorGraphic(ctx: CanvasRenderingContext2D, x: number, y: number) {
  const doorH = 34;

  ctx.fillStyle = '#1e293b';
  ctx.fillRect(x - 14, y - doorH, 28, doorH);

  ctx.fillStyle = '#3b82f6';
  ctx.fillRect(x - 11, y - doorH + 2, 22, doorH - 2);

  ctx.fillStyle = '#e0f2fe';
  ctx.fillRect(x - 6, y - doorH + 6, 12, 10);
  ctx.strokeStyle = '#0284c7';
  ctx.strokeRect(x - 6, y - doorH + 6, 12, 10);

  ctx.fillStyle = '#f59e0b';
  ctx.fillRect(x + 5, y - 12, 3, 2);
}

function drawPixelCoffeeStationGraphic(ctx: CanvasRenderingContext2D, x: number, y: number, label?: string) {
  const topY = y - 18;

  ctx.fillStyle = '#78350f';
  ctx.fillRect(x - 16, topY, 32, 16);
  ctx.fillStyle = '#451a03';
  ctx.fillRect(x - 14, topY + 2, 12, 12);
  ctx.fillRect(x + 2, topY + 2, 12, 12);
  ctx.fillStyle = '#f59e0b';
  ctx.fillRect(x - 4, topY + 6, 1, 4);
  ctx.fillRect(x + 3, topY + 6, 1, 4);

  ctx.fillStyle = '#f8fafc';
  ctx.fillRect(x - 17, topY - 2, 34, 3);

  ctx.fillStyle = '#dc2626';
  ctx.fillRect(x - 10, topY - 16, 14, 14);
  ctx.fillStyle = '#0f172a';
  ctx.fillRect(x - 8, topY - 6, 10, 4);
  ctx.fillStyle = '#f59e0b';
  ctx.fillRect(x - 4, topY - 14, 3, 3);

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(x - 5, topY - 5, 4, 4);

  ctx.fillStyle = '#d97706';
  ctx.fillRect(x + 6, topY - 12, 3, 10);
  ctx.fillStyle = '#16a34a';
  ctx.fillRect(x + 10, topY - 12, 3, 10);

  ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
  ctx.fillRect(Math.round(x - 4), Math.round(topY - 19), 2, 2);
  ctx.fillRect(Math.round(x - 2), Math.round(topY - 22), 2, 2);

  if (label) {
    ctx.fillStyle = '#84cc16';
    ctx.font = 'bold 9px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(label, x, topY - 26);
  }
}

function drawPixelWaterDispenserGraphic(ctx: CanvasRenderingContext2D, x: number, y: number, label?: string) {
  const topY = y - 18;

  ctx.fillStyle = '#f1f5f9';
  ctx.fillRect(x - 8, topY, 16, 20);
  ctx.strokeStyle = '#cbd5e1';
  ctx.strokeRect(x - 8, topY, 16, 20);

  ctx.fillStyle = '#1e293b';
  ctx.fillRect(x - 6, topY + 4, 12, 6);
  ctx.fillStyle = '#ef4444';
  ctx.fillRect(x - 4, topY + 2, 3, 3);
  ctx.fillStyle = '#3b82f6';
  ctx.fillRect(x + 1, topY + 2, 3, 3);

  ctx.fillStyle = '#38bdf8';
  ctx.beginPath();
  ctx.arc(x, topY - 10, 8, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = '#0284c7';
  ctx.stroke();

  ctx.fillStyle = '#e0f2fe';
  ctx.beginPath();
  ctx.arc(x - 2, topY - 12, 2, 0, Math.PI * 2);
  ctx.fill();

  if (label) {
    ctx.fillStyle = '#38bdf8';
    ctx.font = 'bold 9px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(label, x, topY - 22);
  }
}

function drawPixelFridgeGraphic(ctx: CanvasRenderingContext2D, x: number, y: number) {
  const topY = y - 26;

  ctx.fillStyle = '#64748b';
  ctx.fillRect(x - 11, topY, 22, 28);

  ctx.fillStyle = '#94a3b8';
  ctx.fillRect(x - 10, topY + 1, 20, 10);
  ctx.fillRect(x - 10, topY + 12, 20, 15);

  ctx.fillStyle = '#0f172a';
  ctx.fillRect(x + 6, topY + 4, 2, 5);
  ctx.fillRect(x + 6, topY + 16, 2, 7);

  ctx.fillStyle = '#ef4444';
  ctx.fillRect(x - 6, topY + 15, 3, 3);
}

function drawPixelVendingMachineGraphic(ctx: CanvasRenderingContext2D, x: number, y: number) {
  const topY = y - 28;

  ctx.fillStyle = '#1e293b';
  ctx.fillRect(x - 12, topY, 24, 30);

  ctx.fillStyle = '#0f172a';
  ctx.fillRect(x - 10, topY + 2, 16, 20);

  ctx.fillStyle = '#ef4444';
  ctx.fillRect(x - 8, topY + 5, 4, 3);
  ctx.fillRect(x - 2, topY + 5, 4, 3);
  ctx.fillStyle = '#f59e0b';
  ctx.fillRect(x - 8, topY + 11, 4, 3);
  ctx.fillStyle = '#22c55e';
  ctx.fillRect(x - 2, topY + 11, 4, 3);

  ctx.fillStyle = '#f1f5f9';
  ctx.fillRect(x + 7, topY + 6, 3, 6);
  ctx.fillStyle = '#0284c7';
  ctx.fillRect(x + 7, topY + 2, 3, 2);
}

function drawPixelSnackTableGraphic(ctx: CanvasRenderingContext2D, x: number, y: number) {
  const topY = y - 14;

  ctx.fillStyle = '#1e293b';
  ctx.fillRect(x - 18, topY + 6, 4, 12);
  ctx.fillRect(x + 14, topY + 6, 4, 12);

  ctx.fillStyle = '#854d0e';
  ctx.fillRect(x - 22, topY, 44, 6);

  ctx.fillStyle = '#fef08a';
  ctx.fillRect(x - 12, topY - 4, 12, 4);
  ctx.fillStyle = '#ef4444';
  ctx.fillRect(x - 10, topY - 2, 8, 2);

  ctx.fillStyle = '#38bdf8';
  ctx.fillRect(x + 4, topY - 5, 3, 5);
}

function drawPixelSofaGraphic(ctx: CanvasRenderingContext2D, x: number, y: number) {
  const topY = y - 14;

  ctx.fillStyle = '#14532d';
  ctx.fillRect(x - 22, topY, 44, 16);

  ctx.fillStyle = '#166534';
  ctx.fillRect(x - 22, topY - 4, 6, 20);
  ctx.fillRect(x + 16, topY - 4, 6, 20);

  ctx.fillStyle = '#22c55e';
  ctx.fillRect(x - 16, topY - 8, 15, 12);
  ctx.fillRect(x + 1, topY - 8, 15, 12);

  ctx.fillStyle = '#15803d';
  ctx.fillRect(x - 16, topY + 4, 15, 8);
  ctx.fillRect(x + 1, topY + 4, 15, 8);
}

function drawPixelToiletGraphic(ctx: CanvasRenderingContext2D, x: number, y: number, label?: string) {
  const topY = y - 14;

  ctx.fillStyle = '#f8fafc';
  ctx.fillRect(x - 8, topY - 12, 16, 12);
  ctx.strokeStyle = '#cbd5e1';
  ctx.strokeRect(x - 8, topY - 12, 16, 12);

  ctx.fillStyle = '#94a3b8';
  ctx.fillRect(x + 5, topY - 10, 2, 3);

  ctx.fillStyle = '#ffffff';
  ctx.beginPath();
  ctx.ellipse(x, topY + 4, 9, 7, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = '#cbd5e1';
  ctx.stroke();

  ctx.fillStyle = '#38bdf8';
  ctx.beginPath();
  ctx.ellipse(x, topY + 4, 5, 4, 0, 0, Math.PI * 2);
  ctx.fill();

  if (label) {
    ctx.fillStyle = '#ec4899';
    ctx.font = 'bold 9px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(label, x, topY - 16);
  }
}

function drawPixelSinkGraphic(ctx: CanvasRenderingContext2D, x: number, y: number) {
  const topY = y - 16;

  ctx.fillStyle = '#38bdf8';
  ctx.fillRect(x - 8, topY - 18, 16, 14);
  ctx.strokeStyle = '#e0f2fe';
  ctx.strokeRect(x - 8, topY - 18, 16, 14);

  ctx.fillStyle = '#cbd5e1';
  ctx.fillRect(x - 10, topY, 20, 16);

  ctx.fillStyle = '#ffffff';
  ctx.beginPath();
  ctx.ellipse(x, topY + 6, 7, 5, 0, 0, Math.PI * 2);
  ctx.fill();

  ctx.fillStyle = '#0284c7';
  ctx.fillRect(x - 1, topY - 2, 2, 5);
}

function drawPixelWhiteboardGraphic(ctx: CanvasRenderingContext2D, x: number, y: number, label?: string) {
  const topY = y - 28;

  ctx.strokeStyle = '#475569';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(x - 14, y + 4);
  ctx.lineTo(x - 2, topY + 22);
  ctx.moveTo(x + 14, y + 4);
  ctx.lineTo(x + 2, topY + 22);
  ctx.stroke();

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(x - 20, topY, 40, 24);
  ctx.strokeStyle = '#94a3b8';
  ctx.strokeRect(x - 20, topY, 40, 24);

  ctx.fillStyle = '#ef4444';
  ctx.fillRect(x - 14, topY + 4, 8, 6);
  ctx.fillStyle = '#3b82f6';
  ctx.fillRect(x + 4, topY + 4, 8, 6);
  ctx.fillStyle = '#10b981';
  ctx.fillRect(x - 5, topY + 14, 10, 6);

  ctx.strokeStyle = '#64748b';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(x - 6, topY + 7);
  ctx.lineTo(x + 4, topY + 7);
  ctx.stroke();

  if (label) {
    ctx.fillStyle = '#eab308';
    ctx.font = 'bold 9px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(label, x, topY - 4);
  }
}

function drawPixelMeetingTableGraphic(ctx: CanvasRenderingContext2D, x: number, y: number) {
  const topY = y - 10;

  ctx.fillStyle = '#854d0e';
  ctx.beginPath();
  ctx.ellipse(x, topY, 32, 18, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = '#f59e0b';
  ctx.lineWidth = 1.5;
  ctx.stroke();

  const chairPositions = [
    { cx: x - 26, cy: topY - 12 },
    { cx: x, cy: topY - 16 },
    { cx: x + 26, cy: topY - 12 },
    { cx: x - 26, cy: topY + 12 },
    { cx: x, cy: topY + 16 },
    { cx: x + 26, cy: topY + 12 },
  ];

  chairPositions.forEach((pos) => {
    ctx.fillStyle = '#1e293b';
    ctx.beginPath();
    ctx.arc(pos.cx, pos.cy, 5, 0, Math.PI * 2);
    ctx.fill();
  });
}

function drawPixelTVScreenGraphic(ctx: CanvasRenderingContext2D, x: number, y: number) {
  const topY = y - 26;

  ctx.fillStyle = '#0f172a';
  ctx.fillRect(x - 16, topY, 32, 20);

  ctx.fillStyle = '#1e293b';
  ctx.fillRect(x - 14, topY + 2, 28, 16);

  ctx.strokeStyle = '#22c55e';
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(x - 12, topY + 14);
  ctx.lineTo(x - 6, topY + 10);
  ctx.lineTo(x, topY + 12);
  ctx.lineTo(x + 6, topY + 6);
  ctx.lineTo(x + 12, topY + 8);
  ctx.stroke();
}

function drawPixelPrinterGraphic(ctx: CanvasRenderingContext2D, x: number, y: number) {
  const topY = y - 20;

  ctx.fillStyle = '#cbd5e1';
  ctx.fillRect(x - 14, topY, 28, 22);

  ctx.fillStyle = '#475569';
  ctx.fillRect(x - 14, topY - 4, 28, 4);

  ctx.fillStyle = '#0284c7';
  ctx.fillRect(x + 4, topY + 2, 6, 4);

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(x - 10, topY + 10, 16, 2);
  ctx.fillRect(x - 10, topY + 16, 16, 2);
}

function drawPixelFilingCabinetGraphic(ctx: CanvasRenderingContext2D, x: number, y: number) {
  const topY = y - 24;

  ctx.fillStyle = '#64748b';
  ctx.fillRect(x - 10, topY, 20, 26);

  for (let i = 0; i < 3; i++) {
    const dY = topY + 2 + i * 8;
    ctx.fillStyle = '#94a3b8';
    ctx.fillRect(x - 9, dY, 18, 7);
    ctx.fillStyle = '#0f172a';
    ctx.fillRect(x - 3, dY + 3, 6, 2);
  }

  ctx.fillStyle = '#ef4444';
  ctx.fillRect(x - 8, topY - 6, 3, 6);
  ctx.fillStyle = '#f59e0b';
  ctx.fillRect(x - 4, topY - 6, 3, 6);
  ctx.fillStyle = '#10b981';
  ctx.fillRect(x, topY - 6, 3, 6);
}

function drawPixelPlantGraphic(ctx: CanvasRenderingContext2D, x: number, y: number) {
  const topY = y - 12;

  ctx.fillStyle = '#b45309';
  ctx.fillRect(x - 7, topY, 14, 12);

  ctx.fillStyle = '#451a03';
  ctx.fillRect(x - 6, topY - 1, 12, 2);

  ctx.fillStyle = '#15803d';
  ctx.beginPath();
  ctx.arc(x - 5, topY - 8, 7, 0, Math.PI * 2);
  ctx.fill();

  ctx.fillStyle = '#22c55e';
  ctx.beginPath();
  ctx.arc(x + 5, topY - 10, 8, 0, Math.PI * 2);
  ctx.fill();

  ctx.fillStyle = '#86efac';
  ctx.beginPath();
  ctx.arc(x, topY - 14, 9, 0, Math.PI * 2);
  ctx.fill();
}

function drawPixelACGraphic(ctx: CanvasRenderingContext2D, x: number, y: number) {
  const topY = y - 20;

  ctx.fillStyle = '#f8fafc';
  ctx.fillRect(x - 14, topY, 28, 12);
  ctx.strokeStyle = '#cbd5e1';
  ctx.strokeRect(x - 14, topY, 28, 12);

  ctx.fillStyle = '#64748b';
  ctx.fillRect(x - 12, topY + 8, 24, 2);

  ctx.fillStyle = '#22c55e';
  ctx.fillRect(x + 10, topY + 2, 2, 2);
}

function drawPixelVacuumGraphic(ctx: CanvasRenderingContext2D, x: number, y: number) {
  ctx.fillStyle = '#e2e8f0';
  ctx.beginPath();
  ctx.arc(x, y + 6, 9, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = '#64748b';
  ctx.stroke();

  ctx.fillStyle = '#f97316';
  ctx.fillRect(x - 3, y + 4, 6, 3);
}

function drawPixelGenericGraphic(ctx: CanvasRenderingContext2D, x: number, y: number, color: string, icon: string, label: string) {
  ctx.fillStyle = color;
  ctx.fillRect(x - 12, y - 16, 24, 18);
  ctx.font = '12px sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText(icon, x, y - 20);
}

// --- DIBUJO DETALLADO DEL SPRITE PIXEL ART DEL AGENTE ---

function drawAgentSprite(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  agent: AIAgent,
  isSelected: boolean,
  isSimulating: boolean,
  isOccludingHover: boolean = false,
  isHovered: boolean = false
) {
  ctx.save();

  if (isOccludingHover) {
    ctx.globalAlpha = 0.35;
  }

  const bobbing = agent.status === 'walking' ? Math.sin(performance.now() / 100) * 2 : 0;
  const spriteY = y + bobbing;

  ctx.beginPath();
  ctx.ellipse(x, spriteY + 16, 16, 8, 0, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(0, 0, 0, 0.42)';
  ctx.fill();

  if (isSelected) {
    ctx.beginPath();
    ctx.ellipse(x, spriteY + 16, 20, 10, 0, 0, Math.PI * 2);
    ctx.strokeStyle = '#f59e0b';
    ctx.lineWidth = 2.5;
    ctx.stroke();
  } else if (isHovered && isSimulating) {
    ctx.beginPath();
    ctx.ellipse(x, spriteY + 16, 18, 9, 0, 0, Math.PI * 2);
    ctx.strokeStyle = '#06b6d4';
    ctx.lineWidth = 2;
    ctx.setLineDash([4, 3]);
    ctx.stroke();
    ctx.setLineDash([]);
  }

  const isWalking = agent.status === 'walking' || (Boolean(agent.path) && agent.path.length > 0);
  let direction: SpriteDirection = agent.facingDirection || 'front';
  if (agent.path && agent.path.length > 0) {
    const nextStep = agent.path[0];
    const dx = nextStep.col - agent.currentCol;
    const dy = nextStep.row - agent.currentRow;
    direction = getIsometric4Direction(dx, dy, direction);
  }

  const animFrameIndex = isWalking ? Math.floor((performance.now() / 150) % 4) : 0;
  const targetFrameKey = getFrameKey(direction, isWalking, animFrameIndex);

  const spriteSrc =
    agent.spriteSet?.[targetFrameKey] ||
    agent.spriteSet?.front_idle ||
    agent.customImage ||
    generateProceduralSpriteFrame(
      direction,
      isWalking ? (animFrameIndex === 1 ? 'walk1' : animFrameIndex === 2 ? 'walk2' : 'walk3') : 'idle',
      agent.avatarShirtColor,
      agent.hairColor
    );

  if (spriteSrc) {
    const spriteImg = getOrLoadSpriteImage(spriteSrc);
    if (spriteImg && spriteImg.complete && spriteImg.naturalWidth > 0) {
      const renderH = 2 * TILE_HEIGHT;
      const aspectRatio = spriteImg.naturalWidth / spriteImg.naturalHeight;
      const renderW = Math.round(renderH * (aspectRatio || 0.875));

      ctx.imageSmoothingEnabled = false;
      ctx.drawImage(spriteImg, x - renderW / 2, spriteY + 16 - renderH, renderW, renderH);
    } else {
      ctx.fillStyle = agent.avatarShirtColor;
      ctx.fillRect(x - 12, spriteY - 16, 24, 24);
    }
  } else {
    ctx.fillStyle = '#1e293b';
    ctx.fillRect(x - 8, spriteY - 2, 7, 16);
    ctx.fillRect(x + 1, spriteY - 2, 7, 16);

    ctx.fillStyle = '#0f172a';
    ctx.fillRect(x - 9, spriteY + 12, 8, 4);
    ctx.fillRect(x + 1, spriteY + 12, 8, 4);

    ctx.fillStyle = agent.avatarShirtColor;
    ctx.fillRect(x - 12, spriteY - 26, 24, 24);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(x - 2, spriteY - 26, 4, 8);

    ctx.fillStyle = '#fde047';
    ctx.fillRect(x - 10, spriteY - 44, 20, 18);

    ctx.fillStyle = agent.hairColor;
    ctx.fillRect(x - 12, spriteY - 48, 24, 10);

    ctx.fillStyle = '#0f172a';
    ctx.fillRect(x - 6, spriteY - 36, 4, 4);
    ctx.fillRect(x + 2, spriteY - 36, 4, 4);
  }

  // Etiqueta con el nombre (encima del avatar)
  const nameTagBottomY = spriteY - 52;
  const displayNickname = agent.nickname?.trim() || agent.name;
  ctx.font = 'bold 9px sans-serif';
  const nameWidth = ctx.measureText(displayNickname).width;
  const tagW = Math.max(nameWidth + 12, 44);
  const tagX = x - tagW / 2;
  const tagY = nameTagBottomY - 15;
  const tagH = 15;

  ctx.beginPath();
  // NOTA: ctx.roundRect puede no existir en los tipos de TS 3.8 — cast seguro con fallback en runtime
  if ((ctx as any).roundRect) {
    (ctx as any).roundRect(tagX, tagY, tagW, tagH, 4);
  } else {
    ctx.rect(tagX, tagY, tagW, tagH);
  }
  ctx.fillStyle = 'rgba(15, 23, 42, 0.92)';
  ctx.fill();
  ctx.strokeStyle = isSelected ? '#10b981' : 'rgba(255, 255, 255, 0.25)';
  ctx.lineWidth = 1;
  ctx.stroke();

  ctx.fillStyle = '#ffffff';
  ctx.textAlign = 'center';
  ctx.fillText(displayNickname, x, nameTagBottomY - 3.5);

  // Burbuja de pensamiento/acción activa (encima de la etiqueta de nombre)
  if (agent.thoughts && agent.thoughts.length > 0) {
    const latestThought = agent.thoughts[agent.thoughts.length - 1];
    const thoughtText = latestThought.text;

    ctx.font = '8.5px sans-serif';
    const textWidth = ctx.measureText(thoughtText).width;
    const boxW = Math.min(Math.max(textWidth + 12, 48), 130);
    const thoughtBottomY = nameTagBottomY - 19;
    const boxX = x - boxW / 2;
    const boxY = thoughtBottomY - 15;
    const boxH = 15;

    ctx.beginPath();
    if ((ctx as any).roundRect) {
      (ctx as any).roundRect(boxX, boxY, boxW, boxH, 4);
    } else {
      ctx.rect(boxX, boxY, boxW, boxH);
    }
    ctx.fillStyle = 'rgba(255, 255, 255, 0.96)';
    ctx.fill();
    ctx.strokeStyle = '#0284c7';
    ctx.lineWidth = 1;
    ctx.stroke();

    ctx.fillStyle = '#0f172a';
    ctx.fillText(thoughtText.length > 20 ? thoughtText.slice(0, 18) + '..' : thoughtText, x, thoughtBottomY - 3.5);
  }

  ctx.restore();
}

/**
 * Renderiza el overlay de una zona espacial con bordes perimetrales isométricos,
 * relleno translúcido, líneas de acento punteadas, y vértices interactivos en las esquinas.
 */
function drawRoomOverlay(
  ctx: CanvasRenderingContext2D,
  room: OfficeRoom,
  isSelected: boolean,
  agentsCount: number,
  originX: number,
  originY: number,
  showLabel: boolean = true,
  hoveredHandle?: RoomVertexHandle | null,
  activeResizingHandle?: RoomVertexHandle | null,
  isOverlapping: boolean = false,
  isSimulating: boolean = false
) {
  const hw = TILE_WIDTH / 2;
  const hh = TILE_HEIGHT / 2;

  const topTile = gridToIso(room.col, room.row, 0, originX, originY);
  const rightTile = gridToIso(room.col + room.width - 1, room.row, 0, originX, originY);
  const botTile = gridToIso(room.col + room.width - 1, room.row + room.height - 1, 0, originX, originY);
  const leftTile = gridToIso(room.col, room.row + room.height - 1, 0, originX, originY);

  const pTop = { x: topTile.x, y: topTile.y, handle: 'top' as const };
  const pRight = { x: rightTile.x + hw, y: rightTile.y + hh, handle: 'right' as const };
  const pBot = { x: botTile.x, y: botTile.y + TILE_HEIGHT, handle: 'bottom' as const };
  const pLeft = { x: leftTile.x - hw, y: leftTile.y + hh, handle: 'left' as const };

  const centerX = (pTop.x + pBot.x) / 2;
  const centerY = (pTop.y + pBot.y) / 2;

  const baseColor = isOverlapping ? '#f43f5e' : (room.color || '#38bdf8');
  const isCurrentlyResized = Boolean(activeResizingHandle);

  ctx.save();

  if (!isSimulating) {
    ctx.beginPath();
    ctx.moveTo(pTop.x, pTop.y);
    ctx.lineTo(pRight.x, pRight.y);
    ctx.lineTo(pBot.x, pBot.y);
    ctx.lineTo(pLeft.x, pLeft.y);
    ctx.closePath();

    ctx.fillStyle = isOverlapping
      ? 'rgba(244, 63, 94, 0.28)'
      : isCurrentlyResized
      ? `${baseColor}30`
      : isSelected
      ? `${baseColor}20`
      : `${baseColor}0c`;
    ctx.fill();

    ctx.strokeStyle = isOverlapping ? '#f43f5e' : isCurrentlyResized ? '#38bdf8' : isSelected ? baseColor : `${baseColor}cc`;
    ctx.lineWidth = isOverlapping ? 3 : isCurrentlyResized ? 2.5 : isSelected ? 2.2 : 1.5;
    ctx.setLineDash(isOverlapping ? [5, 3] : [7, 4]);
    ctx.stroke();
    ctx.setLineDash([]);

    const corners = [pTop, pRight, pBot, pLeft];
    corners.forEach((c) => {
      const isHovered = hoveredHandle === c.handle;
      const isActive = activeResizingHandle === c.handle;

      ctx.save();
      if (isActive) {
        ctx.beginPath();
        ctx.arc(c.x, c.y, 11, 0, Math.PI * 2);
        ctx.fillStyle = isOverlapping ? 'rgba(244, 63, 94, 0.4)' : `${baseColor}40`;
        ctx.fill();
        ctx.strokeStyle = isOverlapping ? '#f43f5e' : '#38bdf8';
        ctx.lineWidth = 1.5;
        ctx.stroke();

        ctx.beginPath();
        ctx.arc(c.x, c.y, 6, 0, Math.PI * 2);
        ctx.fillStyle = '#ffffff';
        ctx.fill();
        ctx.strokeStyle = isOverlapping ? '#e11d48' : '#0284c7';
        ctx.lineWidth = 2.5;
        ctx.stroke();
      } else if (isHovered) {
        ctx.beginPath();
        ctx.arc(c.x, c.y, 9, 0, Math.PI * 2);
        ctx.fillStyle = `${baseColor}35`;
        ctx.fill();

        ctx.beginPath();
        ctx.arc(c.x, c.y, 5.5, 0, Math.PI * 2);
        ctx.fillStyle = '#ffffff';
        ctx.fill();
        ctx.strokeStyle = baseColor;
        ctx.lineWidth = 2.5;
        ctx.stroke();

        ctx.beginPath();
        ctx.arc(c.x, c.y, 1.8, 0, Math.PI * 2);
        ctx.fillStyle = baseColor;
        ctx.fill();
      } else {
        const r = isSelected ? 4.5 : 3.5;
        ctx.beginPath();
        ctx.arc(c.x, c.y, r, 0, Math.PI * 2);
        ctx.fillStyle = '#ffffff';
        ctx.fill();
        ctx.strokeStyle = baseColor;
        ctx.lineWidth = isSelected ? 2 : 1.5;
        ctx.stroke();
      }
      ctx.restore();
    });

    if (isCurrentlyResized) {
      ctx.save();
      ctx.font = 'bold 9px monospace';
      ctx.fillStyle = isOverlapping ? '#fb7185' : '#38bdf8';
      ctx.strokeStyle = '#0f172a';
      ctx.lineWidth = 3;
      ctx.textAlign = 'center';

      const midTopRight = { x: (pTop.x + pRight.x) / 2, y: (pTop.y + pRight.y) / 2 - 8 };
      ctx.strokeText(`Width: ${room.width}m`, midTopRight.x, midTopRight.y);
      ctx.fillText(`Width: ${room.width}m`, midTopRight.x, midTopRight.y);

      const midTopLeft = { x: (pTop.x + pLeft.x) / 2, y: (pTop.y + pLeft.y) / 2 - 8 };
      ctx.strokeText(`Length: ${room.height}m`, midTopLeft.x, midTopLeft.y);
      ctx.fillText(`Length: ${room.height}m`, midTopLeft.x, midTopLeft.y);
      ctx.restore();
    }
  }

  if (showLabel) {
    const roomTitle = isOverlapping ? `⚠️ Overlap: ${room.name}` : `${room.icon || '📍'} ${room.name}`;

    let subtitle = '';
    if (!isSimulating) {
      subtitle = isOverlapping
        ? 'Not allowed!'
        : `${room.width}x${room.height} (${room.width * room.height}m²)${agentsCount > 0 ? ` • 👤 ${agentsCount}` : ''}`;
    } else if (agentsCount > 0) {
      subtitle = `👤 ${agentsCount} ${agentsCount === 1 ? 'agent' : 'agents'}`;
    }

    const hasSubtitle = Boolean(subtitle);
    ctx.font = 'bold 10px sans-serif';
    const titleWidth = ctx.measureText(roomTitle).width;
    let subWidth = 0;
    if (hasSubtitle) {
      ctx.font = '8px sans-serif';
      subWidth = ctx.measureText(subtitle).width;
    }

    const badgeW = Math.max(titleWidth, subWidth) + (hasSubtitle ? 22 : 20);
    const badgeH = hasSubtitle ? 26 : 20;

    const badgeX = centerX - badgeW / 2;
    const badgeY = centerY - badgeH / 2;

    ctx.shadowColor = 'rgba(0, 0, 0, 0.55)';
    ctx.shadowBlur = 8;
    ctx.shadowOffsetY = 2;

    ctx.fillStyle = isOverlapping ? 'rgba(136, 19, 55, 0.96)' : isCurrentlyResized ? 'rgba(12, 74, 110, 0.95)' : 'rgba(15, 23, 42, 0.88)';
    ctx.beginPath();
    if ((ctx as any).roundRect) {
      (ctx as any).roundRect(badgeX, badgeY, badgeW, badgeH, 6);
    } else {
      ctx.rect(badgeX, badgeY, badgeW, badgeH);
    }
    ctx.fill();

    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;

    ctx.strokeStyle = isOverlapping ? '#f43f5e' : isCurrentlyResized ? '#38bdf8' : isSelected ? baseColor : isSimulating ? `${baseColor}88` : `${baseColor}66`;
    ctx.lineWidth = isOverlapping ? 2.2 : isCurrentlyResized ? 2 : isSelected ? 1.8 : 1.2;
    ctx.stroke();

    if (hasSubtitle) {
      ctx.font = 'bold 9.5px sans-serif';
      ctx.fillStyle = isOverlapping ? '#ffe4e6' : isCurrentlyResized ? '#e0f2fe' : isSelected ? '#ffffff' : '#f1f5f9';
      ctx.textAlign = 'center';
      ctx.fillText(roomTitle, centerX, badgeY + 11);

      ctx.font = '7.5px sans-serif';
      ctx.fillStyle = isOverlapping ? '#fda4af' : isCurrentlyResized ? '#38bdf8' : isSelected ? baseColor : '#94a3b8';
      ctx.fillText(subtitle, centerX, badgeY + 21);
    } else {
      ctx.font = 'bold 9.5px sans-serif';
      ctx.fillStyle = isSelected ? '#ffffff' : '#f1f5f9';
      ctx.textAlign = 'center';
      ctx.fillText(roomTitle, centerX, badgeY + 13.5);
    }
  }

  ctx.restore();
}